import React, { useState, useEffect, useRef } from 'react';
import { useColumnCustomizer } from '../components/ColumnCustomizer';
import * as XLSX from 'xlsx';
import QRCode from 'qrcode';
import { buildQuotationDoc, downloadPdfDoc, imageUrlToDataUrl } from '../utils/pdfDocGen';
import { usePermissions } from '../hooks/usePermissions';
import OnlineStoreSettings from './OnlineStoreSettings';
import { buildFirmAddress } from '../utils/firmAddress';
import { quoteTotals, isGovt, isPassThrough } from '../utils/charges';
import { applySort, makeSortToggle, SortableTh } from '../components/SortableTable';
import ChipOverflowStrip from '../components/ChipOverflowStrip';
import PillTabs from '../components/PillTabs';
import { QuotationShell, QuoteSection, QuotationItemsSection, QuoteLabel, quoteInputStyle, computeQuoteTotals } from '../components/QuotationEditor';

// Every fetch in this file must go through BASE, never a raw literal — a
// hardcoded literal URL works in local dev but is unreachable
// from a real visitor's browser in production (and browsers block it
// outright as mixed content on an HTTPS page), silently failing with a
// generic "Server error" toast. This bug existed in 38 call sites here.
const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Column definitions
const ALL_COLUMNS = [
  { key: 'lead_id', label: 'Lead ID', default: true },
  { key: 'company', label: 'Business Name', default: true },
  { key: 'name', label: 'Contact Person', default: true },
  { key: 'phone', label: 'Phone', default: true },
  { key: 'email', label: 'Email', default: false },
  { key: 'source', label: 'Source', default: true },
  { key: 'referred_by', label: 'Referred By', default: true },
  { key: 'assigned_name', label: 'Assigned To', default: true },
  { key: 'services', label: 'Services', default: false },
  { key: 'stage', label: 'Stage', default: true },
  { key: 'status', label: 'Status', default: true },
  { key: 'created_at', label: 'Created On', default: true },
];

function SmartDatePicker({ label, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => value ? new Date(value).getFullYear() : new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => value ? new Date(value).getMonth() : new Date().getMonth());
  const [mode, setMode] = useState('day'); // 'day' | 'month' | 'year'
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const DAYS = ['Su','Mo','Tu','We','Th','Fr','Sa'];
  const today = new Date(); today.setHours(0,0,0,0);
  const selected = value ? new Date(value + 'T00:00:00') : null;
  const getDays = () => {
    const first = new Date(viewYear, viewMonth, 1).getDay();
    const total = new Date(viewYear, viewMonth + 1, 0).getDate();
    const prev = new Date(viewYear, viewMonth, 0).getDate();
    const cells = [];
    for (let i = first - 1; i >= 0; i--) cells.push({ d: prev - i, curr: false });
    for (let d = 1; d <= total; d++) cells.push({ d, curr: true });
    while (cells.length % 7 !== 0) { cells.push({ d: cells.length - first - total + 1, curr: false }); }
    return cells;
  };
  const selectDay = (d) => {
    const m = String(viewMonth + 1).padStart(2,'0'), dd = String(d).padStart(2,'0');
    onChange(`${viewYear}-${m}-${dd}`);
    setOpen(false);
  };
  const yearRange = Array.from({length: 12}, (_, i) => viewYear - 5 + i);
  const displayVal = value ? new Date(value + 'T00:00:00').toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '';
  return (
    <div className="relative" ref={ref}>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <button type="button" onClick={() => { setOpen(o => !o); setMode('day'); }}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-left flex items-center justify-between hover:border-blue-400 focus:outline-none focus:border-blue-500 bg-white">
        <span className={displayVal ? 'text-gray-800' : 'text-gray-400'}>{displayVal || 'dd/mm/yyyy'}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-[999] w-72 overflow-hidden">
          {/* Header nav */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <button onClick={() => { if(mode==='day') setViewMonth(m => { if(m===0){setViewYear(y=>y-1);return 11;} return m-1; }); else if(mode==='year') setViewYear(y=>y-12); }}
              className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <div className="flex items-center gap-1">
              <button onClick={() => setMode(m => m === 'month' ? 'day' : 'month')}
                className="px-2 py-1 rounded-lg hover:bg-gray-100 text-sm font-semibold text-gray-700">
                {MONTHS[viewMonth]}
              </button>
              <button onClick={() => setMode(m => m === 'year' ? 'day' : 'year')}
                className="px-2 py-1 rounded-lg hover:bg-gray-100 text-sm font-semibold text-gray-700">
                {viewYear}
              </button>
            </div>
            <button onClick={() => { if(mode==='day') setViewMonth(m => { if(m===11){setViewYear(y=>y+1);return 0;} return m+1; }); else if(mode==='year') setViewYear(y=>y+12); }}
              className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>
          {/* Month picker */}
          {mode === 'month' && (
            <div className="grid grid-cols-3 gap-1 p-3">
              {MONTHS.map((m, i) => (
                <button key={m} onClick={() => { setViewMonth(i); setMode('day'); }}
                  className={`py-2 rounded-lg text-sm font-medium transition ${viewMonth === i ? 'text-white' : 'hover:bg-gray-100 text-gray-700'}`}
                  style={viewMonth === i ? { background: '#0f1f45' } : {}}>
                  {m}
                </button>
              ))}
            </div>
          )}
          {/* Year picker */}
          {mode === 'year' && (
            <div className="grid grid-cols-3 gap-1 p-3">
              {yearRange.map(y => (
                <button key={y} onClick={() => { setViewYear(y); setMode('day'); }}
                  className={`py-2 rounded-lg text-sm font-medium transition ${viewYear === y ? 'text-white' : 'hover:bg-gray-100 text-gray-700'}`}
                  style={viewYear === y ? { background: '#0f1f45' } : {}}>
                  {y}
                </button>
              ))}
            </div>
          )}
          {/* Day picker */}
          {mode === 'day' && (
            <div className="p-3">
              <div className="grid grid-cols-7 mb-1">
                {DAYS.map(d => <div key={d} className="text-center text-xs font-medium text-gray-400 py-1">{d}</div>)}
              </div>
              <div className="grid grid-cols-7">
                {getDays().map((cell, i) => {
                  const cellDate = new Date(viewYear, viewMonth, cell.d); cellDate.setHours(0,0,0,0);
                  const isSel = selected && cell.curr && cellDate.getTime() === selected.getTime();
                  const isToday = cell.curr && cellDate.getTime() === today.getTime();
                  return (
                    <button key={i} onClick={() => cell.curr && selectDay(cell.d)}
                      className={`h-8 w-full rounded-lg text-xs font-medium transition ${!cell.curr ? 'text-gray-300 cursor-default' : isSel ? 'text-white' : isToday ? 'border border-blue-400 text-blue-600' : 'hover:bg-gray-100 text-gray-700'}`}
                      style={isSel ? { background: '#0f1f45' } : {}}>
                      {cell.d}
                    </button>
                  );
                })}
              </div>
              {value && (
                <button onClick={() => { onChange(''); setOpen(false); }}
                  className="w-full mt-2 py-1.5 text-xs text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition">
                  Clear date
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Single source of truth for lead stages — was previously duplicated (with
// diverging color maps) across kanban columns, quick-select, pipeline
// columns, form select and 3 separate color helpers.
// Fallback only — shown for the instant before the firm's own Lead Stages
// (Settings → CRM Settings → Lead Stages) finish loading, or if a brand-new
// firm's seed hasn't landed yet. The live, firm-editable list (fetched into
// `leadStages` state in the main CRM component) is the source of truth
// everywhere else. Field names (`name` not `label`) match the API shape.
const LEAD_STAGES = [
  { value: 'new',          name: 'New',          color: '#6366f1' },
  { value: 'contacted',    name: 'Contacted',    color: '#d97706' },
  { value: 'proposal',     name: 'Proposal',     color: '#7c3aed' },
  { value: 'negotiation',  name: 'Negotiation',  color: '#ea580c' },
  { value: 'won',          name: 'Won',          color: '#16a34a', is_system: true },
  { value: 'lost',         name: 'Lost',         color: '#dc2626', is_system: true },
  { value: 'not_relevant', name: 'Not Relevant', color: '#64748b' },
];
// Only Won/Lost are ever hardcoded-terminal — automation (auto Client+Task
// creation, referral pipeline close-out) keys off exactly these two literal
// values, so a lead sitting in either can't be deleted. Every other stage,
// including "Not Relevant", is a fully ordinary, firm-editable row now.
const TERMINAL_STAGES = ['won', 'lost'];

// Searchable picker for an EXISTING client — used by Add Lead so a quotation / new
// service can be raised for a customer who is already on the books, without
// re-typing them or creating a duplicate client. Picking one prefills the lead
// form and links the lead to that client.
function ExistingClientPicker({ clients, selected, onPick, onClear, darkMode = false, T }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const term = q.trim().toLowerCase();
  const shown = (clients || []).filter(c => !term ||
    `${c.client_name || ''} ${c.file_number || ''} ${c.mobile || ''} ${c.pan || ''} ${c.gstin || ''}`.toLowerCase().includes(term)).slice(0, 40);

  if (selected) {
    return (
      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:10, background: darkMode ? 'rgba(14,165,233,0.12)' : '#f0f9ff', border:'1px solid rgba(14,165,233,0.35)' }}>
        <span style={{ width:30, height:30, borderRadius:'50%', background:'#0ea5e9', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:13, flexShrink:0 }}>{(selected.client_name || '?').charAt(0).toUpperCase()}</span>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13, fontWeight:700, color:T.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{selected.client_name}</div>
          <div style={{ fontSize:11.5, color:T.text3 }}>{[selected.file_number, selected.mobile].filter(Boolean).join(' · ') || 'Existing client'} — details filled below; this lead is linked to the client.</div>
        </div>
        <button type="button" onClick={onClear} style={{ fontSize:12, fontWeight:600, color:'#0369a1', background:'none', border:'none', cursor:'pointer' }}>Change</button>
      </div>
    );
  }
  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', borderRadius:10, fontSize:13, cursor:'pointer', border:`1.5px solid ${open ? '#3b82f6' : T.border2}`, background:T.bg2, color:T.text3, textAlign:'left' }}>
        <span style={{ display:'flex', alignItems:'center', gap:8 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          Search your clients by name, file no., mobile or PAN…
        </span>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
      </button>
      {open && (
        <div style={{ position:'absolute', left:0, right:0, top:'calc(100% + 6px)', zIndex:60, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, overflow:'hidden', boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.15)' }}>
          <div style={{ padding:10, borderBottom:`1px solid ${T.border}` }}>
            <input autoFocus type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="Type to search…"
              style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'8px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' }} />
          </div>
          <div style={{ maxHeight:260, overflowY:'auto', padding:5 }}>
            {shown.length === 0 ? (
              <p style={{ fontSize:12.5, color:T.text3, textAlign:'center', padding:'18px 0', margin:0 }}>{(clients || []).length === 0 ? 'No clients yet.' : 'No client matches.'}</p>
            ) : shown.map(c => (
              <button key={c.id} type="button" onClick={() => { onPick(c); setOpen(false); setQ(''); }}
                style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'8px 10px', borderRadius:8, border:'none', background:'transparent', cursor:'pointer', textAlign:'left' }}
                onMouseEnter={e => e.currentTarget.style.background = T.bg3} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span style={{ width:28, height:28, borderRadius:'50%', background:'#e0f2fe', color:'#0369a1', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:12, flexShrink:0 }}>{(c.client_name || '?').charAt(0).toUpperCase()}</span>
                <span style={{ flex:1, minWidth:0 }}>
                  <span style={{ display:'block', fontSize:13, fontWeight:600, color:T.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{c.client_name}</span>
                  <span style={{ display:'block', fontSize:11.5, color:T.text3 }}>{[c.file_number, c.mobile, c.client_type].filter(Boolean).join(' · ')}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Multi-select user picker (one lead, several owners). Shows each user's role
// (Manager, Staff, Article, Admin… whatever is defined under Settings → Roles).
function UserMultiSelect({ users, value, onChange, darkMode = false, T }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const ids = (value || []).map(String);
  const list = (users || []).filter(u => u.is_active !== false);
  const picked = ids.map(id => list.find(u => String(u.id) === id)).filter(Boolean);
  const term = q.trim().toLowerCase();
  const shown = list.filter(u => !term || `${u.full_name || ''} ${u.role || ''}`.toLowerCase().includes(term));
  const toggle = (u) => onChange(ids.includes(String(u.id)) ? (value || []).filter(x => String(x) !== String(u.id)) : [...(value || []), u.id]);
  const roleChip = (r) => r ? <span style={{ fontSize:10.5, fontWeight:600, padding:'1px 8px', borderRadius:20, background: darkMode ? 'rgba(148,163,184,0.15)' : '#eef2f7', color:T.text2, textTransform:'capitalize', whiteSpace:'nowrap' }}>{r}</span> : null;
  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ width:'100%', minHeight:40, border:`1px solid ${open ? '#3b82f6' : T.inputBorder}`, borderRadius:9, padding:'6px 12px', background:T.inputBg, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, boxSizing:'border-box', textAlign:'left' }}>
        <span style={{ display:'flex', flexWrap:'wrap', gap:6, flex:1, minWidth:0 }}>
          {picked.length === 0 ? <span style={{ fontSize:13, color:T.text3 }}>Unassigned — select one or more users</span> : picked.map(u => (
            <span key={u.id} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'3px 6px 3px 10px', borderRadius:20, fontSize:12, fontWeight:600, background: darkMode ? 'rgba(59,130,246,0.14)' : '#eff6ff', color: darkMode ? '#93c5fd' : '#1d4ed8', border:`1px solid ${darkMode ? 'rgba(59,130,246,0.3)' : '#bfdbfe'}` }}>
              {u.full_name}{u.role ? <span style={{ fontWeight:500, opacity:0.75, textTransform:'capitalize' }}>· {u.role}</span> : null}
              <span role="button" onClick={(e) => { e.stopPropagation(); toggle(u); }} style={{ width:15, height:15, borderRadius:'50%', display:'inline-flex', alignItems:'center', justifyContent:'center', background: darkMode ? 'rgba(59,130,246,0.25)' : '#bfdbfe', cursor:'pointer', fontSize:10, lineHeight:1 }}>×</span>
            </span>
          ))}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" style={{ flexShrink:0, transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
      </button>
      {open && (
        <div style={{ position:'absolute', left:0, right:0, top:'calc(100% + 6px)', zIndex:9999, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, overflow:'hidden', boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.14)' }}>
          <div style={{ padding:8, borderBottom:`1px solid ${T.border}` }}>
            <input autoFocus type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or role…"
              style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:12.5, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' }} />
          </div>
          <div className="cx-noscroll" style={{ maxHeight:240, overflowY:'auto', padding:5 }}>
            {shown.length === 0 && <p style={{ fontSize:12, color:T.text3, padding:10, margin:0 }}>No user found</p>}
            {shown.map(u => {
              const sel = ids.includes(String(u.id));
              return (
                <button key={u.id} type="button" onClick={() => toggle(u)}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'8px 10px', borderRadius:8, border:'none', cursor:'pointer', textAlign:'left', background: sel ? (darkMode ? 'rgba(59,130,246,0.12)' : '#eff6ff') : 'transparent' }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = T.bg3; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                  <span style={{ width:16, height:16, borderRadius:4, border:`2px solid ${sel ? '#3b82f6' : T.border2}`, background: sel ? '#3b82f6' : 'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {sel && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5"><polyline points="20 6 9 17 4 12"/></svg>}
                  </span>
                  <span style={{ width:26, height:26, borderRadius:'50%', background:'#e0e7ff', color:'#3730a3', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:11.5, flexShrink:0 }}>{(u.full_name || '?').charAt(0).toUpperCase()}</span>
                  <span style={{ flex:1, minWidth:0, fontSize:13, fontWeight: sel ? 700 : 500, color:T.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{u.full_name}</span>
                  {roleChip(u.role)}
                </button>
              );
            })}
          </div>
          <div style={{ padding:'8px 12px', borderTop:`1px solid ${T.border}`, background:T.bg3, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:11.5, color:T.text3 }}>{picked.length} selected{picked.length > 1 ? ' · first is the primary owner' : ''}</span>
            <span>
              {picked.length > 0 && <button type="button" onClick={() => onChange([])} style={{ fontSize:12, color:'#ef4444', background:'none', border:'none', cursor:'pointer', marginRight:10 }}>Clear</button>}
              <button type="button" onClick={() => setOpen(false)} style={{ fontSize:12, fontWeight:600, color:'#3b82f6', background:'none', border:'none', cursor:'pointer' }}>Done</button>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Generic picklist dropdown with inline "+ Add new" creation — used for both
// Lead Stage and Lead Source pickers. Mirrors Clients.js's TypeDropdown
// (same visual language app-wide for this kind of firm-editable picklist),
// generalized to optionally show a color dot per item (stages have one,
// sources don't) and to disable creating/duplicating a protected value.
function PicklistDropdown({ items, value, onChange, onCreate, placeholder = 'Select...', addLabel = 'Add new', showColor = false, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setAdding(false); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const list = items || [];
  const selected = list.find(t => String(t.value) === String(value));

  const submitNew = async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      const created = await onCreate(name);
      if (created?.value) onChange(created.value);
      setNewName(''); setAdding(false); setOpen(false);
    } finally { setBusy(false); }
  };

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          width:'100%', border:`1px solid ${open ? '#3b82f6' : T.inputBorder}`, borderRadius:9,
          padding:'9px 12px', fontSize:13, textAlign:'left', display:'flex', alignItems:'center',
          justifyContent:'space-between', background:T.inputBg, cursor:'pointer', minHeight:38,
          transition:'border-color 0.15s', boxSizing:'border-box',
        }}>
        <span style={{ display:'flex', alignItems:'center', gap:7, color: selected ? T.text : T.text3, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
          {showColor && selected && <span style={{ width:9, height:9, borderRadius:'50%', background:selected.color||'#64748b', flexShrink:0 }} />}
          {selected ? selected.name : placeholder}{selected && selected.sub && <span style={{ fontSize:11, color:T.text3, flexShrink:0 }}>{selected.sub}</span>}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"
          style={{ flexShrink:0, marginLeft:6, transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', left:0, right:0, background:T.bg2,
          border:`1px solid ${T.border2}`, borderRadius:12, zIndex:9999, overflow:'hidden',
          boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.14)',
        }}>
          <div className="cx-noscroll" style={{ maxHeight:210, overflowY:'auto', padding:5 }}>
            {list.map(t => {
              const sel = String(t.value) === String(value);
              return (
                <button key={t.value} type="button" onClick={() => { onChange(t.value); setOpen(false); }}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, color:T.text, textAlign:'left', fontWeight: sel ? 700 : 500, background: sel ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent', transition:'background 0.12s' }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = T.bg3; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                  {showColor && <span style={{ width:9, height:9, borderRadius:'50%', background:t.color||'#64748b', flexShrink:0 }} />}
                  <span style={{ flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{t.name}</span>{t.sub && <span style={{ fontSize:11, color:T.text3, flexShrink:0, fontWeight:500 }}>{t.sub}</span>}
                  {sel && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                </button>
              );
            })}
            {list.length === 0 && <p style={{ fontSize:12, color:T.text3, padding:'10px' }}>None configured</p>}
          </div>

          {onCreate && <div style={{ borderTop:`1px solid ${T.border}`, padding:5 }}>
            {adding ? (
              <div style={{ display:'flex', gap:6, alignItems:'center', padding:'2px' }}>
                <input autoFocus type="text" value={newName} placeholder="Type a name"
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitNew(); } if (e.key === 'Escape') { setAdding(false); setNewName(''); } }}
                  style={{ flex:1, border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:12, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
                <button type="button" onClick={submitNew} disabled={busy || !newName.trim()}
                  style={{ padding:'7px 12px', borderRadius:8, border:'none', background:'#0f1f45', color:'#fff', fontSize:12, fontWeight:600, cursor: busy||!newName.trim() ? 'default' : 'pointer', opacity: busy||!newName.trim() ? 0.5 : 1, flexShrink:0 }}>
                  {busy ? '…' : 'Add'}
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setAdding(true)}
                style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', background:'transparent', cursor:'pointer', fontSize:12.5, fontWeight:600, color:'#3b82f6', textAlign:'left' }}
                onMouseEnter={e => e.currentTarget.style.background = darkMode?'rgba(59,130,246,0.1)':'#eff6ff'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                {addLabel}
              </button>
            )}
          </div>}
        </div>
      )}
    </div>
  );
}

// Small inline Feather-style SVG icon set — replaces the emoji used in the
// Follow Up / Quotation tabs so icons look consistent with the rest of the app.
const IconPhone = (p) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>;
const IconMail = (p) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;
const IconWhatsApp = (p) => <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm5.8 14.07c-.24.68-1.4 1.3-1.94 1.38-.5.08-1.12.11-1.81-.11-.42-.13-.95-.31-1.64-.6-2.88-1.24-4.76-4.14-4.9-4.33-.14-.19-1.17-1.56-1.17-2.98 0-1.41.74-2.1 1-2.39.27-.29.58-.36.78-.36h.55c.18 0 .42-.07.65.5.24.58.82 2 .89 2.15.07.14.12.31.02.5-.1.19-.15.31-.29.48-.15.17-.31.38-.44.51-.15.15-.3.31-.13.6.17.29.76 1.25 1.63 2.02 1.12 1 2.06 1.31 2.35 1.46.29.15.46.13.63-.05.17-.19.72-.84.92-1.13.19-.29.38-.24.65-.14.27.1 1.7.8 1.99.95.29.14.48.22.55.34.07.13.07.72-.17 1.4z"/></svg>;
const IconMeeting = (p) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const IconEdit = (p) => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const IconCheck = (p) => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" {...p}><polyline points="20 6 9 17 4 12"/></svg>;
const IconX = (p) => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" {...p}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IconDownload = (p) => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>;
const IconSend = (p) => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>;
const IconTrash = (p) => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>;

const typeIcon = (type) => ({
  call: <IconPhone />, email: <IconMail />, whatsapp: <IconWhatsApp />, meeting: <IconMeeting />,
}[type] || <IconPhone />);

// ── Quotation item math + normalization — shared by LeadDetailModal (the
// service-picker form/PDF) and QuotationView (the standalone list), so both
// compute totals and build invoice line items identically. ──────────────
const calcItemAmount = (it) => Number(it.quantity || 1) * Number(it.rate || 0);
const calcItemDiscount = (it) => {
  const amt = calcItemAmount(it);
  if (it.discount_type === 'percentage') return amt * Number(it.discount_value || 0) / 100;
  if (it.discount_type === 'fixed') return Number(it.discount_value || 0);
  return 0;
};
const calcItemNet = (it) => calcItemAmount(it) - calcItemDiscount(it);
const calcSubtotal = (items) => (items || []).reduce((sum, it) => sum + calcItemNet(it), 0);

// A lead quotation line is stored as { quantity, rate, discount_type, discount_value, … }; the shared
// quotation editor (components/QuotationEditor.js) edits one Amount + one ₹ Discount per line.
const toEditorLine = (it) => {
  const qty = Number(it.quantity || 1);
  const disc = it.discount_type === 'percentage' ? calcItemDiscount(it) : (it.discount_type === 'fixed' ? it.discount_value : '');
  return {
    service_id: it.service_id || null, particular: it.particular || '', description: it.description || '', sac_code: it.sac_code || '',
    amount: qty === 1 ? it.rate : qty * Number(it.rate || 0),
    discount: disc === 0 ? '' : disc,
    charge_type: it.charge_type === 'government' ? 'government' : 'professional',
    gst_applies: it.charge_type === 'government' ? (it.gst_applies === true || it.gst_applies === 'true') : true,
  };
};
const fromEditorLine = (e) => ({
  service_id: e.service_id || null, particular: e.particular, description: e.description || '', sac_code: e.sac_code || '',
  quantity: 1, rate: e.amount,
  discount_type: Number(e.discount) > 0 ? 'fixed' : 'none', discount_value: e.discount === '' ? 0 : e.discount,
  charge_type: e.charge_type === 'government' ? 'government' : 'professional',
  gst_applies: e.charge_type === 'government' ? (e.gst_applies === true || e.gst_applies === 'true') : true,
});

// Local (IST) calendar date as YYYY-MM-DD — toISOString() is UTC and gives yesterday before 5:30 AM.
const ymdLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Normalizes a proposal's stored items, falling back to a single legacy item
// built from title/amount/content for quotations created before the
// service-picker rework (so old records still render/edit/convert sensibly).
const itemsFromProposal = (p) => {
  if (Array.isArray(p.items) && p.items.length) return p.items;
  if (p.title) return [{ service_id: null, particular: p.title, description: p.content || '', quantity: 1, rate: p.amount || 0, discount_type: 'none', discount_value: 0 }];
  return [];
};

// Full itemization for a "Create Invoice from Quotation" handoff — every
// line item, with quantity/rate/discount carried over (not collapsed into
// one lump-sum line, and not silently dropping any discount that was set),
// and linked to the Task auto-created for that service when the quotation
// was accepted (proposal.task_ids[i]) so the invoice marks the real task
// "billed" instead of leaving it task-less/untracked.
const buildInvoiceItemsFromProposal = (proposal) => { let ti = 0; return itemsFromProposal(proposal).map((it) => ({
  task_id: isGovt(it) ? null : ((proposal.task_ids && proposal.task_ids[ti++]) || null),
  charge_type: isGovt(it) ? 'government' : 'professional',
  gst_applies: isGovt(it) ? (it.gst_applies === true || it.gst_applies === 'true') : true,
  from_quotation: true,
  particular: it.particular || '',
  description: it.description || '',
  quantity: it.quantity || 1,
  rate: it.rate || 0,
  discount_type: it.discount_type || 'none',
  discount_value: it.discount_value || 0,
})); };

// Finds the client already converted from this lead, or converts it now if
// accepting the quotation somehow didn't (defensive fallback) — used by both
// quotation→invoice handoff sites so the invoice form opens with the client
// pre-selected instead of forcing a manual pick that then wipes the items.
async function getClientForLead(lead, token) {
  const headers = { 'Authorization': `Bearer ${token}` };
  const clientsRes = await fetch(`${BASE}/api/clients`, { headers });
  const clients = await clientsRes.json();
  // A lead raised for an existing client always resolves to that client.
  if (lead.client_id && Array.isArray(clients)) {
    const linked = clients.find(c => String(c.id) === String(lead.client_id));
    if (linked) return linked;
  }
  const existing = Array.isArray(clients) ? clients.find(c => String(c.converted_from_lead) === String(lead.id)) : null;
  if (existing) return existing;

  const convertRes = await fetch(`${BASE}/api/leads/${lead.id}/convert`, { method: 'PUT', headers });
  const convertData = await convertRes.json();
  if (convertRes.ok && convertData.client) return convertData.client;

  const retryRes = await fetch(`${BASE}/api/clients`, { headers });
  const retryClients = await retryRes.json();
  return Array.isArray(retryClients) ? retryClients.find(c => String(c.converted_from_lead) === String(lead.id)) : null;
}

// Lightweight toast system — replaces native alert() for save/delete/error
// feedback. Each component that needs it calls useToasts() and renders
// <ToastStack toasts={toasts} /> once near its root.
function useToasts() {
  const [toasts, setToasts] = useState([]);
  const showToast = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, message, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 4000);
  };
  return [toasts, showToast];
}

// ── Phone helpers — mirrors normalize_phone_in() in the database. Indian numbers
// are stored as the 10-digit national number (the whole app, incl. wa.me/91…
// links, relies on that) and always DISPLAYED as "+91 88699 53866". A number
// typed with a non-India "+" country code keeps its "+CC…" form.
const normalizePhoneIN = (raw) => {
  const t = String(raw || '').trim();
  const d = t.replace(/\D/g, '');
  if (!d) return t;
  if (t.startsWith('+') && !d.startsWith('91')) return '+' + d;
  if (d.length === 10) return d;
  if (d.length === 11 && d.startsWith('0')) return d.slice(-10);
  if (d.length === 12 && d.startsWith('91')) return d.slice(-10);
  if (d.length === 13 && d.startsWith('091')) return d.slice(-10);
  return t;
};
const formatPhone = (raw) => {
  const p = normalizePhoneIN(raw);
  if (!p) return '';
  return /^\d{10}$/.test(p) ? `+91 ${p.slice(0, 5)} ${p.slice(5)}` : p;
};
const waNumber = (raw) => {
  const p = normalizePhoneIN(raw);
  return /^\d{10}$/.test(p) ? '91' + p : p.replace(/\D/g, '');
};

// ── Follow-up helpers — shared by the lead's Follow Up tab and the Follow-up View ──
// A follow-up with no time of day is treated as due at 10:00 (matches the
// backend reminder job, jobs/followupReminders.js).
const FOLLOWUP_REMIND_OPTIONS = [
  { v: 0, l: 'At the scheduled time' }, { v: 15, l: '15 minutes before' }, { v: 30, l: '30 minutes before' },
  { v: 60, l: '1 hour before' }, { v: 120, l: '2 hours before' }, { v: 1440, l: '1 day before' },
];
const followupDueAt = (f) => new Date(`${String(f.follow_up_date).slice(0, 10)}T${(f.follow_up_time || '10:00').slice(0, 5)}:00`);
const isFollowupOverdue = (f) => f.status === 'pending' && followupDueAt(f) < new Date();
const isFollowupToday = (f) => f.status === 'pending' && followupDueAt(f).toDateString() === new Date().toDateString();
const isoDateOffset = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
const FOLLOWUP_QUICK_DATES = [['Today', 0], ['Tomorrow', 1], ['In 3 days', 3], ['Next week', 7]];

// Small "Snooze" menu: push a pending follow-up later without opening the edit form.
function SnoozeMenu({ onSnooze }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const opts = [['1 hour', { minutes: 60 }], ['3 hours', { minutes: 180 }], ['Tomorrow', { to_tomorrow: true }], ['In 3 days', { minutes: 4320 }]];
  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button type="button" onClick={() => setOpen(o => !o)} className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 font-medium">⏱ Snooze</button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(15,31,69,0.15)', zIndex: 60, minWidth: 120, padding: 4 }}>
          {opts.map(([label, body]) => (
            <button key={label} type="button" onClick={() => { setOpen(false); onSnooze(body); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', fontSize: 12, border: 'none', background: 'none', cursor: 'pointer', borderRadius: 6, color: '#334155' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>{label}</button>
          ))}
        </div>
      )}
    </span>
  );
}

// CRM action permissions (Settings → Manage Access → CRM). The sidebar only
// checks "view"; every Create / Edit / Delete / Export control inside CRM goes
// through this. Flags stay false until the role's permissions have loaded (so
// action buttons never flash for a user who lacks them); admin is always full.
// Buttons are HIDDEN (not disabled) when a permission is missing; deny() is the
// belt-and-braces guard for a handler that fires anyway (e.g. an already-open
// modal after an admin revokes access) — it toasts and the caller skips the API.
// Backend enforcement is a separate task (RBAC Phase 2).
function useCrmAccess(showToast) {
  const { can, loading, role } = usePermissions();
  const ready = role === 'admin' || !loading;
  const flags = {
    canCreate: ready && can('crm', 'create'),
    canEdit:   ready && can('crm', 'edit'),
    canDelete: ready && can('crm', 'delete'),
    canExport: ready && can('crm', 'export'),
    canFollowupDelete: ready && can('crm.followups_delete'),
  };
  const deny = (action) => {
    showToast(`You don't have permission to ${action} leads. Ask your admin.`, 'error');
    return false;
  };
  // Cross-module check for actions that touch another module's data
  // (Convert to Client → clients.create, Create Task → tasks.create).
  const canIn = (module, action) => ready && can(module, action);
  return { ...flags, deny, canIn };
}

function ToastStack({ toasts }) {
  if (!toasts || !toasts.length) return null;
  return (
    <div style={{ position:'fixed', top:20, right:20, zIndex:100000, display:'flex', flexDirection:'column', gap:8 }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          minWidth:260, maxWidth:380, padding:'12px 16px', borderRadius:10,
          background: t.type==='error' ? '#fef2f2' : '#f0fdf4',
          border: `1px solid ${t.type==='error' ? '#fecaca' : '#bbf7d0'}`,
          color: t.type==='error' ? '#991b1b' : '#166534',
          fontSize:13, fontWeight:500, boxShadow:'0 8px 24px rgba(0,0,0,0.16)',
          display:'flex', alignItems:'center', gap:8,
        }}>
          {t.type==='error' ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" style={{flexShrink:0}}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          )}
          <span>{t.message}</span>
        </div>
      ))}
    </div>
  );
}

function ConfirmModal({ open, title, message, onConfirm, onCancel, confirmLabel='Confirm', confirmColor='#ef4444' }) {
  if (!open) return null;
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:99999, padding:16 }}>
      <div style={{ background:'#fff', borderRadius:16, width:'min(92vw,420px)', overflow:'hidden', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding:'20px 24px', borderBottom:'1px solid #f1f5f9' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
            <div style={{ width:36, height:36, borderRadius:10, background:'#fef2f2', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
            </div>
            <h3 style={{ fontSize:15, fontWeight:700, color:'#0f1f45', margin:0 }}>{title}</h3>
          </div>
          <p style={{ fontSize:13, color:'#64748b', margin:0, lineHeight:1.6 }}>{message}</p>
        </div>
        <div style={{ display:'flex', gap:10, padding:'16px 24px' }}>
          <button onClick={onCancel} style={{ flex:1, padding:'9px 0', borderRadius:9, border:'1px solid #e2e8f0', background:'#f8fafc', color:'#4a5568', fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancel</button>
          <button onClick={onConfirm} style={{ flex:1, padding:'9px 0', borderRadius:9, border:'none', background:confirmColor, color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function CRM({ darkMode = false, T: theme }) {
  const T = theme || {
    bg:'#f1f5f9', bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0',
    text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9',
    inputBg:'#fff', inputBorder:'#e2e8f0',
  };
  const [confirm, setConfirm] = useState(null);
  const showConfirm = (title, message, onConfirm, confirmLabel='Confirm', confirmColor='#ef4444') => setConfirm({ title, message, onConfirm, confirmLabel, confirmColor });
  const [toasts, showToast] = useToasts();
  const { canCreate, canEdit, canDelete, canExport, canIn, deny } = useCrmAccess(showToast);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editLead, setEditLead] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const cc = useColumnCustomizer(ALL_COLUMNS, { visKey: 'crmVisibleColumns', orderKey: 'crmColumnOrder', T, darkMode });
  const { visibleColumns, orderedVisibleKeys } = cc;
  const [filters, setFilters] = useState({
    stage: '', status: '', source: '', assigned_to: '',
    date_from: '', date_to: '', date_preset: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [users, setUsers] = useState([]);
  // Column sorting — same headers/behaviour as Finance → Invoices. Newest first by default.
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const onSort = makeSortToggle(sortKey, setSortKey, setSortDir);
  // Firm-editable picklists (Settings → CRM Settings → Lead Stages / Lead
  // Sources) — replace the old hardcoded LEAD_STAGES array and static
  // source lists everywhere in this file. 'won'/'lost' come back flagged
  // is_system:true and are treated as protected in the UI.
  const [leadStages, setLeadStages] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const stagesForUI = leadStages.length ? leadStages : LEAD_STAGES;
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const [openActionLeadId, setOpenActionLeadId] = useState(null);
  const [actionPos, setActionPos] = useState({ top: 0, right: 0 });
  const [createTaskLead, setCreateTaskLead] = useState(null);
  const [assignLead, setAssignLead] = useState(null);
  const token = localStorage.getItem('token');

  // ← NEW: cache followups + proposals in parent so tab switches don't re-fetch
  const [allFollowups, setAllFollowups] = useState([]);
  const [allProposals, setAllProposals] = useState([]);

  const fetchLeads = async () => {
    try {
      const response = await fetch(`${BASE}/api/leads`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setLeads(Array.isArray(data) ? data : []);
    } catch (error) { console.error(error); }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${BASE}/api/leads/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

    const fetchLeadStages = () => {
    fetch(`${BASE}/api/leads/stages/all`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setLeadStages(Array.isArray(d) ? d : []))
      .catch(() => {});
  };
  const fetchLeadSources = () => {
    fetch(`${BASE}/api/leads/sources/all`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setLeadSources(Array.isArray(d) ? d : []))
      .catch(() => {});
  };
  const createLeadStage = async (name) => {
    if (!canCreate) { deny('create'); return null; }
    const res = await fetch(`${BASE}/api/leads/stages`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ name }) });
    const d = await res.json();
    if (!res.ok) { showToast(d.message || 'Could not add stage', 'error'); return null; }
    fetchLeadStages();
    return d;
  };
  const createLeadSource = async (name) => {
    if (!canCreate) { deny('create'); return null; }
    const res = await fetch(`${BASE}/api/leads/sources`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ name }) });
    const d = await res.json();
    if (!res.ok) { showToast(d.message || 'Could not add source', 'error'); return null; }
    fetchLeadSources();
    return d;
  };

  // Parallel fetch: leads + stats + followups + proposals + users all at once on mount
  useEffect(() => {
    fetchLeads();
    fetchStats();
    fetchLeadStages();
    fetchLeadSources();

    // Pre-fetch followups
    fetch(`${BASE}/api/followups/all`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => setAllFollowups(Array.isArray(d) ? d : []))
      .catch(console.error);

    // Pre-fetch proposals
    fetch(`${BASE}/api/proposals/all`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => setAllProposals(Array.isArray(d) ? d : []))
      .catch(console.error);

    // Pre-fetch users for lead action menu
    fetch(`${BASE}/api/users`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => setUsers(Array.isArray(d) ? d : []))
      .catch(() => setUsers([]));
  }, []);


  const applyDatePreset = (preset) => {
    const now = new Date();
    let from = '', to = '';
    const today = ymdLocal(now);
    if (preset === 'today') {
      from = to = today;
    } else if (preset === 'this_week') {
      const day = now.getDay();
      const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (day === 0 ? 6 : day - 1));
      from = ymdLocal(monday);
      to = today;
    } else if (preset === 'this_month') {
      from = ymdLocal(new Date(now.getFullYear(), now.getMonth(), 1));
      to = today;
    } else if (preset === 'last_month') {
      from = ymdLocal(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      to = ymdLocal(new Date(now.getFullYear(), now.getMonth(), 0));
    } else if (preset === 'this_fy') {
      const fyStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
      from = `${fyStart}-04-01`;
      to = today;
    } else if (preset === 'last_fy') {
      const fyStart = (now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1) - 1;
      from = `${fyStart}-04-01`;
      to = `${fyStart + 1}-03-31`;
    }
    setFilters(f => ({ ...f, date_from: from, date_to: to, date_preset: preset }));
  };

  // Every filter except Stage — the stage chips count against this set.
  const leadsBeforeStage = leads.filter(lead => {
    const q = searchTerm.trim().toLowerCase();
    const qDigits = q.replace(/[^0-9]/g, '');
    const matchSearch = !q ||
      lead.name?.toLowerCase().includes(q) ||
      lead.company?.toLowerCase().includes(q) ||
      lead.email?.toLowerCase().includes(q) ||
      lead.lead_id?.toLowerCase().includes(q) ||
      lead.referred_by_name?.toLowerCase().includes(q) ||
      lead.reference_name?.toLowerCase().includes(q) ||
      (qDigits.length >= 3 && String(lead.phone || '').replace(/[^0-9]/g, '').includes(qDigits.length > 10 ? qDigits.slice(-10) : qDigits));
    const matchStatus = !filters.status || lead.status === filters.status;
    const matchSource = !filters.source || lead.source === filters.source;
    const matchAssigned = !filters.assigned_to || (filters.assigned_to === 'none'
      ? !lead.assigned_to && !(lead.assignee_ids || []).length
      : String(lead.assigned_to) === String(filters.assigned_to) || (lead.assignee_ids || []).map(String).includes(String(filters.assigned_to)));
    const matchDateFrom = !filters.date_from || new Date(lead.created_at) >= new Date(filters.date_from + 'T00:00:00');
    const matchDateTo = !filters.date_to || new Date(lead.created_at) <= new Date(filters.date_to + 'T23:59:59');
    return matchSearch && matchStatus && matchSource && matchAssigned && matchDateFrom && matchDateTo;
  });
  const filteredLeads = filters.stage ? leadsBeforeStage.filter(lead => lead.stage === filters.stage) : leadsBeforeStage;
  // Stage chips on top of the leads table (shared ChipOverflowStrip — same as Tasks / Services / Clients)
  const stageCounts = leadsBeforeStage.reduce((acc, l) => { acc[l.stage] = (acc[l.stage] || 0) + 1; return acc; }, {});
  const stageChips = stagesForUI
    .map(st => ({ key: st.value, name: st.name, color: st.color, count: stageCounts[st.value] || 0 }))
    .filter(st => st.count > 0 || st.key === filters.stage);

  const LEAD_SORT_COLUMNS = [
    { key: 'lead_id', sortType: 'string' },
    { key: 'company', sortType: 'string' },
    { key: 'name', sortType: 'string' },
    { key: 'phone', sortType: 'string' },
    { key: 'email', sortType: 'string' },
    { key: 'source', sortType: 'string', get: l => (leadSources.find(x => x.value === l.source) || {}).name || l.source },
    { key: 'referred_by', sortType: 'string', get: l => l.referred_by_name || l.referred_by || l.reference_name },
    { key: 'assigned_name', sortType: 'string', get: l => ((l.assignees || [])[0] || {}).name || l.assigned_name },
    { key: 'services', sortType: 'string' },
    // stage sorts in pipeline order (New → … → Won / Lost), not alphabetically
    { key: 'stage', sortType: 'number', get: l => { const i = stagesForUI.findIndex(x => x.value === l.stage); return i < 0 ? 999 : i; } },
    { key: 'status', sortType: 'string' },
    { key: 'created_at', sortType: 'date' },
  ];
  const sortedLeads = applySort(filteredLeads, LEAD_SORT_COLUMNS, sortKey, sortDir);

  const stageColor = (stage) => {
    const colors = {
      new: 'bg-blue-100 text-blue-700',
      contacted: 'bg-yellow-100 text-yellow-700',
      proposal: 'bg-purple-100 text-purple-700',
      negotiation: 'bg-orange-100 text-orange-700',
      won: 'bg-green-100 text-green-700',
      lost: 'bg-red-100 text-red-700',
      not_relevant: 'bg-slate-100 text-slate-600',
    };
    return colors[stage] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata'
    });
  };

  const formatDateTime = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata'
  });
};

  const exportToExcel = () => {
    if (!canExport) { deny('export'); return; }
    const exportData = filteredLeads.map(lead => ({
      'Lead ID': lead.lead_id || '',
      'Business Name': lead.company || '',
      'Contact Person': lead.name || '',
      'Email': lead.email || '',
      'Phone': lead.phone || '',
      'Source': lead.source || '',
      'Referred By': lead.referred_by_name ? `${lead.referred_by_name} (${lead.referred_by})` : (lead.referred_by || lead.reference_name || ''),
      'Stage': stagesForUI.find(st => st.value === lead.stage)?.name || lead.stage || '',
      'Status': lead.status || '',
      'GST Type': lead.gst_type || '',
      'GSTIN': lead.gstin || '',
      'Services': lead.services || '',
      'Assigned To': (lead.assignees||[]).length ? lead.assignees.map(a=>a.name).join(', ') : (lead.assigned_name || ''),
      'Created Date': formatDate(lead.created_at),
      'Notes': lead.notes || '',
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    ws['!cols'] = Object.keys(exportData[0] || {}).map(k => ({ wch: Math.max(k.length + 2, 16) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Leads');
    XLSX.writeFile(wb, `ChanakyaX_Leads_${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.xlsx`);
  };


  const activeFiltersCount = ['stage', 'status', 'source', 'assigned_to'].filter(k => filters[k] !== '').length + ((filters.date_from || filters.date_to) ? 1 : 0);
  const refreshCRM = () => {
  fetchLeads();
  fetchStats();
};
  // Leads, stats and the Follow-up / Quotation lists after anything changed in a lead
  // (quotation accepted → lead Won, first follow-up from Add Lead, etc.)
  const refreshAll = () => {
    fetchLeads();
    fetchStats();
    fetch(`${BASE}/api/followups/all`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setAllFollowups(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${BASE}/api/proposals/all`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setAllProposals(Array.isArray(d) ? d : [])).catch(() => {});
  };

const updateLeadQuick = async (lead, patch) => {
  if (!canEdit) { deny('edit'); return false; }
  const updated = {
    ...lead,
    ...patch,
    assigned_to: patch.assigned_to !== undefined ? (patch.assigned_to || null) : (lead.assigned_to || null),
    status: patch.status || lead.status || 'active',
    stage: patch.stage || lead.stage || 'new'
  };

  try {
    const res = await fetch(`${BASE}/api/leads/${lead.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(updated)
    });
    const data = await res.json();
    if (!res.ok) {
      showToast(data.message || 'Could not update lead', 'error');
      return false;
    }
    setOpenActionLeadId(null);
    refreshCRM();
    return data;
  } catch (e) {
    showToast('Server error while updating lead', 'error');
    return false;
  }
};

const handleStageChangeFromMenu = async (lead, stage) => {
  if (!canEdit) { deny('edit'); return; }
  // A lead marked Won with no service on it means the resulting client/task
  // has no idea which actual Services Master entry it's for — block it here
  // rather than letting that ambiguity through.
  if (stage === 'won' && !(lead.services || '').trim()) {
    showToast('Add at least one service to this lead (Edit Lead → Services) before marking it Won.', 'error');
    return;
  }
  const data = await updateLeadQuick(lead, { stage });
  // Backend now auto-creates the Client + Tasks the moment a lead (with no
  // quotation) goes Won directly, so there's nothing left to confirm here —
  // just mirror the quotation-accept flow's staggered success toasts.
  if (data && data.won_client) {
    showToast(data.won_client.existing ? `Lead won — linked to existing client "${data.won_client.client_name}".` : `Lead won — client "${data.won_client.client_name}" created.`, 'success');
    if (data.created_task_ids && data.created_task_ids.length) {
      setTimeout(() => showToast(`${data.created_task_ids.length} task${data.created_task_ids.length > 1 ? 's' : ''} created for the accepted service${data.created_task_ids.length > 1 ? 's' : ''}.`), 400);
    }
  }
};

const handleDeleteLead = async (lead) => {
  if (!canDelete) { deny('delete'); return; }
  if (TERMINAL_STAGES.includes(lead.stage)) {
    showToast(`${stagesForUI.find(s => s.value === lead.stage)?.name || 'This'} leads cannot be deleted.`, 'error');
    return;
  }

  showConfirm('Delete Lead', `Delete lead ${lead.lead_id || ''}? This cannot be undone.`, async () => {
  try {
    const res = await fetch(`${BASE}/api/leads/${lead.id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json();
    if (!res.ok) {
      showToast(data.message || 'Could not delete lead', 'error');
      return;
    }
    setOpenActionLeadId(null);
    refreshCRM();
  } catch (e) {
    showToast('Server error while deleting lead', 'error');
  }
  });
};

const handleConvertLead = async (lead) => {
  // Convert creates a Client, so it needs CRM edit AND clients create.
  if (!canEdit) { deny('edit'); return; }
  if (!canIn('clients', 'create')) { showToast("You don't have permission to create clients, so this lead can't be converted. Ask your admin.", 'error'); return; }
  try {
    const res = await fetch(`${BASE}/api/leads/${lead.id}/convert`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json();
    if (!res.ok) {
      showToast(data.message || 'Could not convert lead', 'error');
      return;
    }
    showToast(data.message || 'Lead converted to client successfully!');
    if (data.created_task_ids && data.created_task_ids.length) {
      setTimeout(() => showToast(`${data.created_task_ids.length} task${data.created_task_ids.length > 1 ? 's' : ''} created for the accepted service${data.created_task_ids.length > 1 ? 's' : ''}.`), 400);
    }
    setOpenActionLeadId(null);
    refreshCRM();
  } catch (e) {
    showToast('Server error while converting lead', 'error');
  }
};


  return (
    <div style={{
      height:'100vh', padding:24, display:'flex', flexDirection:'column', overflow:'hidden', boxSizing:'border-box',
      background: darkMode
        ? 'radial-gradient(ellipse at 80% 0%, rgba(249,115,22,0.07) 0%, transparent 60%), radial-gradient(ellipse at 20% 100%, rgba(15,31,69,0.4) 0%, transparent 60%), #0a0f1e'
        : 'radial-gradient(ellipse at 80% 0%, rgba(249,115,22,0.05) 0%, transparent 50%), radial-gradient(ellipse at 0% 100%, rgba(15,31,69,0.04) 0%, transparent 50%), #f4f7ff',
    }}>
      <ConfirmModal open={!!confirm} title={confirm?.title||''} message={confirm?.message||''} onConfirm={()=>{confirm?.onConfirm();setConfirm(null);}} onCancel={()=>setConfirm(null)} confirmLabel={confirm?.confirmLabel||'Confirm'} confirmColor={confirm?.confirmColor||'#ef4444'} />
      <ToastStack toasts={toasts} />
      {/* Row 1 — counters, with Export / Add Lead alongside */}
      <div style={{ display:'flex', alignItems:'stretch', gap:12, marginBottom:10 }}>
        <div style={{ flex:1, minWidth:0, display:'grid', gridTemplateColumns:'repeat(5,minmax(0,1fr))', gap:12 }}>
          {[
            { label: 'Total Leads',     value: stats.total || 0,                    color:'#6366f1', glow:'rgba(99,102,241,0.15)'  },
            { label: 'Converted',       value: stats.converted || 0,                color:'#10b981', glow:'rgba(16,185,129,0.15)'  },
            { label: 'Open',            value: stats.open || 0,                     color:'#f59e0b', glow:'rgba(245,158,11,0.15)'  },
            { label: 'Lost',            value: stats.lost || 0,                     color:'#ef4444', glow:'rgba(239,68,68,0.15)'   },
            { label: 'Conversion Rate', value:`${stats.conversionRate || '0.00'}%`, color:'#f97316', glow:'rgba(249,115,22,0.15)'  },
          ].map((stat) => (
            <div key={stat.label} style={{
              background: darkMode
                ? `linear-gradient(135deg, ${T.bg2} 0%, ${T.bg3} 100%)`
                : `linear-gradient(135deg, #fff 0%, #f8f9ff 100%)`,
              border: `1px solid ${T.cardBorder}`,
              borderRadius:12, padding:'12px 16px',
              boxShadow: darkMode
                ? `0 4px 20px ${stat.glow}, 0 1px 3px rgba(0,0,0,0.3)`
                : `0 4px 20px ${stat.glow}, 0 1px 3px rgba(15,31,69,0.06)`,
              position:'relative', overflow:'hidden',
            }}>
              {/* Glow dot */}
              <div style={{
                position:'absolute', top:-16, right:-16, width:60, height:60,
                borderRadius:'50%', background:stat.glow, filter:'blur(12px)',
              }}/>
              <p style={{ fontSize:22, fontWeight:800, color:stat.color, margin:0, lineHeight:1 }}>{stat.value}</p>
              <p style={{ fontSize:11, color:T.text3, margin:'5px 0 0', fontWeight:500 }}>{stat.label}</p>
            </div>
          ))}
        </div>
        {(canExport || canCreate) && (
          <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', gap:8, flexShrink:0 }}>
            {canExport && (
              <button onClick={exportToExcel} title="Export the shown leads to Excel"
                style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'6px 12px', borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:12.5, fontWeight:500, cursor:'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor='#3b82f6'; e.currentTarget.style.color='#3b82f6'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor=T.border2; e.currentTarget.style.color=T.text2; }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Export
              </button>
            )}
            {canCreate && (
              <button onClick={() => setShowAddModal(true)}
                style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'7px 14px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:12.5, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add Lead
              </button>
            )}
          </div>
        )}
      </div>

      {/* Row 2 — views (same tab-card pattern as Tasks / Services / Clients) */}
      <div style={{ marginBottom:10, padding:'6px 8px 6px 6px', background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:12, flexShrink:0 }}>
        <PillTabs
          tabs={[
            { key: 'list',        label: 'List View', count: leads.length },
            { key: 'pipeline',    label: 'Pipeline View' },
            { key: 'followups',   label: 'Follow-up View' },
            { key: 'quotations',  label: 'Quotation View' },
            { key: 'performance', label: 'Performance View' },
            { key: 'store',       label: 'Online Store' },
          ]}
          activeKey={activeTab} onChange={setActiveTab} T={T} darkMode={darkMode} />
      </div>

      <div style={{ flex:1, minHeight:0, display:'flex', flexDirection:'column', overflow:'hidden' }}>

      {/* LIST VIEW */}
      {activeTab === 'list' && (
        <>
          {/* Row 3 — search + filters */}
          <div style={{ background:T.bg2, borderRadius:12, border:`1px solid ${T.cardBorder}`, padding:10, marginBottom:10, flexShrink:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <input type="text" placeholder="Search by name, company, phone, lead ID..."
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                style={{ flex:1, border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'8px 14px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }} />
              <button onClick={() => setShowFilters(f => !f)} style={{
                display:'flex', alignItems:'center', gap:7, padding:'8px 14px',
                borderRadius:9, fontSize:13, fontWeight:500, cursor:'pointer', transition:'all 0.15s',
                border: showFilters ? '1.5px solid #3b82f6' : `1.5px solid ${T.border2}`,
                background: showFilters ? (darkMode ? 'rgba(59,130,246,0.15)' : '#eff6ff') : T.bg2,
                color: showFilters ? '#3b82f6' : T.text2,
              }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                Filters
                {activeFiltersCount > 0 && <span style={{ background:'#3b82f6', color:'#fff', fontSize:10, borderRadius:'50%', width:16, height:16, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700 }}>{activeFiltersCount}</span>}
              </button>
              <p style={{ fontSize:12, color:T.text3, whiteSpace:'nowrap', margin:0 }}>{filteredLeads.length} found</p>
            </div>

            {showFilters && (
              <div style={{ marginTop:14, paddingTop:14, borderTop:`1px solid ${T.border}`, display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Stage</label>
                  <select value={filters.stage} onChange={e => setFilters(f => ({ ...f, stage: e.target.value }))}
                    style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }}>
                    <option value="">All Stages</option>
                    {stagesForUI.map(s => (
                      <option key={s.value} value={s.value}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Status</label>
                  <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                    style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }}>
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Source</label>
                  <select value={filters.source} onChange={e => setFilters(f => ({ ...f, source: e.target.value }))}
                    style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }}>
                    <option value="">All Sources</option>
                    {leadSources.map(s => (
                      <option key={s.value} value={s.value}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Assigned To</label>
                  <select value={filters.assigned_to} onChange={e => setFilters(f => ({ ...f, assigned_to: e.target.value }))}
                    style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }}>
                    <option value="">Everyone</option>
                    <option value="none">Unassigned</option>
                    {users.filter(u => u.is_active !== false).map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Date Preset</label>
                  <select value={filters.date_preset} onChange={e => applyDatePreset(e.target.value)}
                    style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }}>
                    <option value="">Select Period</option>
                    <option value="today">Today</option>
                    <option value="this_week">This Week</option>
                    <option value="this_month">This Month</option>
                    <option value="last_month">Last Month</option>
                    <option value="this_fy">This FY (Apr-Mar)</option>
                    <option value="last_fy">Last FY</option>
                  </select>
                </div>
                <SmartDatePicker label="From Date" value={filters.date_from}
                  onChange={v => setFilters(f => ({ ...f, date_from: v, date_preset: '' }))} />
                <SmartDatePicker label="To Date" value={filters.date_to}
                  onChange={v => setFilters(f => ({ ...f, date_to: v, date_preset: '' }))} />
                <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'flex-end' }}>
                  <button onClick={() => setFilters({ stage:'', status:'', source:'', assigned_to:'', date_from:'', date_to:'', date_preset:'' })}
                    style={{ padding:'7px 16px', borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:12, fontWeight:500, cursor:'pointer' }}>
                    Clear Filters
                  </button>
                </div>
              </div>
            )}
          </div>
          {/* Row 4 — leads table, stage chips on top */}
          <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, boxShadow: darkMode ? '0 4px 24px rgba(0,0,0,0.3)' : '0 2px 12px rgba(15,31,69,0.06)', overflow:'hidden', flex:1, minHeight:0, display:'flex', flexDirection:'column' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderBottom:`1px solid ${T.border}` }}>
            <ChipOverflowStrip
              items={[{ key:'__all', name:'All Stages', count: leadsBeforeStage.length }, ...stageChips]}
              activeKey={filters.stage || '__all'}
              onSelect={k => setFilters(f => ({ ...f, stage: k === '__all' ? '' : k }))}
              T={T} darkMode={darkMode} orderKey="crm-stages" />
          </div>
          <div style={{ flex:1, minHeight:0, overflowX:'auto', overflowY:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', position:'sticky', top:0, zIndex:10 }}>
                <tr>
                  {orderedVisibleKeys.map(key => {
                    const col = ALL_COLUMNS.find(c => c.key === key);
                    return <SortableTh key={key} label={col?.label} sortKey={key} activeKey={sortKey} dir={sortDir} onSort={onSort} style={{ padding:'10px 12px' }} resizeId="crm-leads" />;
                  })}
                  {/* Actions stays pinned to the right edge so it is never scrolled out of view */}
                  <th style={{ position:'sticky', right:0, zIndex:11, padding:'10px 12px', textAlign:'left', background:'#16295a', boxShadow:'-8px 0 10px -8px rgba(0,0,0,0.35)', whiteSpace:'nowrap' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:10, fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>Actions {cc.menu}</span>
                  </th>
                </tr>              
                </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="20" style={{ padding:'32px 16px', textAlign:'center', color:T.text3 }}>Loading...</td></tr>
                ) : filteredLeads.length === 0 ? (
                  <tr><td colSpan="20" style={{ padding:'32px 16px', textAlign:'center', color:T.text3 }}>No leads found.</td></tr>
                ) : (
                  sortedLeads.map((lead, idx) => (
                    <tr key={lead.id} style={{
                      borderBottom:`1px solid ${T.border}`,
                      background: darkMode
                        ? (idx % 2 === 0 ? T.bg2 : T.bg3)
                        : (idx % 2 === 0 ? '#fff' : '#fafbff'),
                      transition:'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = darkMode ? '#1e3a5f' : '#f0f4ff'}
                    onMouseLeave={e => e.currentTarget.style.background = darkMode ? (idx%2===0?T.bg2:T.bg3) : (idx%2===0?'#fff':'#fafbff')}>

                      {orderedVisibleKeys.map(key => (
                        <td key={key} style={{ padding:'10px 12px', fontSize:13 }}>
                          {key === 'stage' ? (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${stageColor(lead.stage)}`}>{stagesForUI.find(st => st.value === lead.stage)?.name || lead.stage || 'New'}</span>
                          ) : key === 'company' && lead.client_id ? (
                            <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                              <span style={{ color:T.text2 }}>{lead.company || '-'}</span>
                              <span title={`Raised for existing client ${lead.linked_client_name || ''} ${lead.linked_client_file_number ? '(' + lead.linked_client_file_number + ')' : ''}`} style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:20, whiteSpace:'nowrap', color:'#0369a1', background:'rgba(14,165,233,0.12)' }}>Existing client</span>
                            </span>
                          ) : key === 'phone' ? (
                            <span style={{ color:T.text2, whiteSpace:'nowrap' }}>{formatPhone(lead.phone) || '-'}</span>
                          ) : key === 'referred_by' && !lead.referred_by && !lead.submitted_by_partner_id && lead.reference_name ? (
                            <span style={{ display:'inline-flex', flexDirection:'column', lineHeight:1.25 }}>
                              <span style={{ fontWeight:600, color:T.text }}>{lead.reference_name}</span>
                              <span style={{ fontSize:11, color:T.text3 }}>Reference</span>
                            </span>
                          ) : key === 'referred_by' && (lead.submitted_by_partner_id || lead.referred_by) ? (
                            <span style={{ display:'inline-flex', alignItems:'center', gap:8, whiteSpace:'nowrap' }}>
                              <span style={{ display:'inline-flex', flexDirection:'column', lineHeight:1.25 }}>
                                <span style={{ fontWeight:600, color:T.text }}>{lead.referred_by_name || lead.referred_by || '—'}</span>
                                {lead.referred_by_name && lead.referred_by && <span style={{ fontSize:11, color:T.text3 }}>{lead.referred_by}</span>}
                              </span>
                              {lead.submitted_by_partner_id && <span title="Submitted by the referral partner from their portal" style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:20, whiteSpace:'nowrap',
                                color: lead.referred_by ? '#c2410c' : '#b45309', background: lead.referred_by ? 'rgba(249,115,22,0.12)' : 'rgba(245,158,11,0.15)' }}>
                                {lead.referred_by ? 'Partner' : 'Partner · verify'}
                              </span>}
                            </span>
                          ) : key === 'status' ? (
                            <span style={{
  display:'inline-flex', alignItems:'center', gap:6,
  padding:'4px 10px', borderRadius:6,
  background: lead.status==='active'?(darkMode?'rgba(16,185,129,0.1)':'rgba(16,185,129,0.08)'):(darkMode?'rgba(239,68,68,0.1)':'rgba(239,68,68,0.08)'),
  border:`1px solid ${lead.status==='active'?(darkMode?'rgba(16,185,129,0.3)':'rgba(16,185,129,0.25)'):(darkMode?'rgba(239,68,68,0.3)':'rgba(239,68,68,0.25)')}`,
}}>
  <span style={{ position:'relative', width:7, height:7, flexShrink:0 }}>
    <span style={{ position:'absolute', inset:0, borderRadius:'50%', background: lead.status==='active'?'#10b981':'#ef4444', animation: lead.status==='active'?'cx-pulse 2s ease-in-out infinite':'none' }}/>
    <span style={{ position:'absolute', inset:0, borderRadius:'50%', background: lead.status==='active'?'#10b981':'#ef4444' }}/>
  </span>
  <span style={{ fontSize:12, fontWeight:600, color: lead.status==='active'?(darkMode?'#34d399':'#059669'):(darkMode?'#f87171':'#dc2626') }}>
    {lead.status}
  </span>
</span>
                          ) : key === 'lead_id' ? (
                            <button onClick={() => setSelectedLead(lead)}
                              style={{ color:'#3b82f6', fontWeight:600, background:'none', border:'none', cursor:'pointer', fontSize:13 }}>
                              {lead[key]}
                            </button>
                          ) : key === 'created_at' ? (
                            <span style={{ color:T.text3, whiteSpace:'nowrap' }}>{formatDate(lead[key])}</span>
                          ) : key === 'assigned_name' ? (
                            lead.assigned_name
                              ? <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, background: darkMode?'rgba(59,130,246,0.15)':'#eff6ff', color:'#3b82f6', fontWeight:600, whiteSpace:'nowrap' }} title={(lead.assignees||[]).map(a=>a.name).join(', ')}>{lead.assigned_name}{(lead.assignees||[]).length > 1 ? ` +${lead.assignees.length - 1}` : ''}</span>
                              : <span style={{ color:T.text3 }}>-</span>
                          ) : key === 'services' ? (
                            lead.services ? (
                              <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                                {lead.services.split(',').slice(0,2).map((s,i) => s.trim() && (
                                  <span key={i} style={{ padding:'2px 7px', borderRadius:4, fontSize:11, background: darkMode?'rgba(139,92,246,0.15)':'#f5f3ff', color:'#7c3aed' }}>{s.trim()}</span>
                                ))}
                                {lead.services.split(',').length > 2 && <span style={{ fontSize:11, color:T.text3 }}>+{lead.services.split(',').length-2}</span>}
                              </div>
                            ) : <span style={{ color:T.text3 }}>-</span>
                          ) : (
                            <span style={{ color:T.text2 }}>{lead[key] || '-'}</span>
                          )}
                        </td>
                      ))}
                      <td style={{ position:'sticky', right:0, zIndex:5, padding:'10px 12px', background:'inherit', boxShadow:'-8px 0 10px -8px rgba(15,31,69,0.14)' }}>
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            const r = e.currentTarget.getBoundingClientRect();
                            // Clamp so the menu never renders past the bottom of the
                            // viewport (it's position:fixed, so an overflow there was
                            // simply invisible/unreachable, not scrollable into view —
                            // this is what made it look like it "disappeared" for rows
                            // near the bottom of the list).
                            const estimatedMenuHeight = 340;
                            const top = Math.min(r.bottom + 4, Math.max(8, window.innerHeight - estimatedMenuHeight - 8));
                            setActionPos({ top, right: window.innerWidth - r.right });
                            setOpenActionLeadId(openActionLeadId === lead.id ? null : lead.id);
                          }}
                          style={{ width:30, height:30, borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', transition:'all 0.15s' }}
                          onMouseEnter={e=>{ e.currentTarget.style.background=T.bg3; e.currentTarget.style.color=T.text2; }}
                          onMouseLeave={e=>{ e.currentTarget.style.background=T.bg2; e.currentTarget.style.color=T.text3; }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}

      {/* Action Kebab Dropdown — fixed position outside table */}
      {openActionLeadId && (() => {
        const lead = leads.find(l => l.id === openActionLeadId);
        if (!lead) return null;
        const isTerminal = TERMINAL_STAGES.includes(lead.stage);
        const selRow = { flex:1, border:'none', background:'none', color:T.text, fontSize:13, fontWeight:500, outline:'none', cursor:'pointer' };
        const rowBtn = { width:'100%', textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', background:'none', fontSize:13, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', gap:8 };
        const items = [
          { label:'View', color:'#3b82f6', hoverBg: darkMode?'rgba(59,130,246,0.12)':'#eff6ff', onClick:() => { setSelectedLead(lead); setOpenActionLeadId(null); }, icon:<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></> },
          { label:'Edit', show:canEdit, color:T.text2, hoverBg:T.bg3, onClick:() => { setEditLead(lead); setOpenActionLeadId(null); }, icon:<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></> },
          { label:'Assign User', show:canEdit, color:T.text2, hoverBg:T.bg3, onClick:() => { setAssignLead(lead); setOpenActionLeadId(null); }, icon:<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></> },
          { label:'Create Task', show:canIn('tasks','create'), color:T.text2, hoverBg:T.bg3, onClick:() => { setCreateTaskLead(lead); setOpenActionLeadId(null); }, icon:<><rect x="9" y="2" width="6" height="4" rx="1"/><path d="M9 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2h-2"/><polyline points="9 12 11 14 15 10"/></> },
          ...(lead.stage === 'won' && !lead.converted_to_client && canEdit && canIn('clients', 'create') ? [
            { label:'Convert to Client', color:'#16a34a', hoverBg: darkMode?'rgba(22,163,74,0.12)':'#f0fdf4', onClick:() => { handleConvertLead(lead); setOpenActionLeadId(null); }, icon:<polyline points="20 6 9 17 4 12"/> },
          ] : []),
        ];
        return (
          <div onMouseLeave={() => setOpenActionLeadId(null)}
            style={{ position:'fixed', top:actionPos.top, right:actionPos.right, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow:darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 8px 24px rgba(15,31,69,0.12)', zIndex:9999, width:190, padding:6 }}>
            {items.filter(item => item.show !== false).map(item => (
              <button key={item.label} onClick={item.onClick} style={{ ...rowBtn, color:item.color }}
                onMouseEnter={e => e.currentTarget.style.background=item.hoverBg}
                onMouseLeave={e => e.currentTarget.style.background='none'}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{item.icon}</svg>
                {item.label}
              </button>
            ))}

            {(canEdit || canDelete) && <div style={{ height:1, background:T.border, margin:'4px 0' }}/>}

            {/* Stage / Status — compact select rows, same visual weight as the buttons above.
                Editing a lead's stage/status needs CRM edit. */}
            {canEdit && <><div style={{ ...rowBtn, cursor:'default' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
              <select value={lead.stage || 'new'} onChange={e => handleStageChangeFromMenu(lead, e.target.value)} style={selRow}>
                {stagesForUI.map(s => <option key={s.value} value={s.value}>{s.name}</option>)}
              </select>
            </div>
            <div style={{ ...rowBtn, cursor:'default' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" style={{flexShrink:0}}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              <select value={lead.status || 'active'} onChange={e => updateLeadQuick(lead, { status: e.target.value })} style={selRow}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div></>}

            {canEdit && canDelete && <div style={{ height:1, background:T.border, margin:'4px 0' }}/>}

            {/* Delete */}
            {canDelete && <button
              onClick={() => { handleDeleteLead(lead); setOpenActionLeadId(null); }}
              disabled={isTerminal}
              style={{ ...rowBtn, cursor:isTerminal?'not-allowed':'pointer', color:isTerminal?T.text3:'#ef4444', opacity:isTerminal?0.5:1 }}
              onMouseEnter={e=>{ if(!isTerminal) e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2'; }}
              onMouseLeave={e=>e.currentTarget.style.background='none'}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
              Delete
            </button>}
          </div>
        );
      })()}

      {/* Create Task Modal */}
      {createTaskLead && canIn('tasks', 'create') && (
        <CreateTaskFromLeadModal
          lead={createTaskLead}
          token={token}
          onClose={() => setCreateTaskLead(null)}
          onSuccess={() => { setCreateTaskLead(null); fetchLeads(); }}
          darkMode={darkMode}
          T={T}
        />
      )}

      {/* Assign User Modal */}
      {assignLead && canEdit && (
        <AssignLeadModal
          lead={assignLead}
          token={token}
          onClose={() => setAssignLead(null)}
          onSuccess={() => { setAssignLead(null); refreshCRM(); }}
          darkMode={darkMode}
          T={T}
        />
      )}

      {/* PIPELINE VIEW */}
      {activeTab === 'pipeline' && (
        <div style={{ display:'flex', gap:16, overflowX:'auto', overflowY:'hidden', flex:1, minHeight:0, paddingBottom:8 }}>
          {stagesForUI.map(({ value: stage, name: label, color }) => {
            const sc = { bg: darkMode ? `${color}22` : `${color}15`, color, border: darkMode ? `${color}55` : `${color}33` };
            return (
              <div key={stage} style={{ flexShrink:0, width:240, display:'flex', flexDirection:'column', minHeight:0 }}>
                <div style={{ borderRadius:10, padding:'10px 14px', marginBottom:10, background:sc.bg, border:`1px solid ${sc.border}` }}>
                  <h3 style={{ fontWeight:700, fontSize:13, color:sc.color, margin:0 }}>{label}</h3>
                  <p style={{ fontSize:11, color:sc.color, opacity:0.75, margin:'3px 0 0' }}>{filteredLeads.filter(l=>l.stage===stage).length} leads</p>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:8, flex:1, minHeight:0, overflowY:'auto', scrollbarWidth:'none', paddingBottom:4 }}>
                  {filteredLeads.filter(l=>l.stage===stage).map((lead) => (
                    <div key={lead.id} onClick={() => setSelectedLead(lead)} style={{
                      background: T.cardBg, border:`1px solid ${T.cardBorder}`,
                      borderRadius:10, padding:12, cursor:'pointer', transition:'all 0.15s',
                      boxShadow: darkMode?'0 2px 8px rgba(0,0,0,0.3)':'0 1px 4px rgba(15,31,69,0.06)',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = darkMode?'0 4px 16px rgba(0,0,0,0.4)':'0 4px 16px rgba(15,31,69,0.12)'; e.currentTarget.style.borderColor='#f97316'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = darkMode?'0 2px 8px rgba(0,0,0,0.3)':'0 1px 4px rgba(15,31,69,0.06)'; e.currentTarget.style.borderColor=T.cardBorder; }}>
                      <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>{lead.company || lead.name}</p>
                      <p style={{ fontSize:11, color:T.text2, margin:'4px 0 0' }}>{lead.name}</p>
                      <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>{formatPhone(lead.phone) || '-'}</p>
                      <p style={{ fontSize:11, color:'#3b82f6', margin:'2px 0 0' }}>{lead.lead_id}</p>
                      {lead.assigned_name && <p style={{ fontSize:11, color:'#16a34a', margin:'2px 0 0' }}>👤 {(lead.assignees || []).length > 1 ? lead.assignees.map(a => a.name).join(', ') : lead.assigned_name}</p>}
                    </div>
                  ))}
                  {filteredLeads.filter(l=>l.stage===stage).length === 0 && (
                    <div style={{ background:'none', border:`2px dashed ${T.border2}`, borderRadius:10, padding:12, textAlign:'center' }}>
                      <p style={{ fontSize:11, color:T.text3 }}>No leads</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* FOLLOW-UP VIEW */}
      {activeTab === 'followups' && (
        <div style={{ flex:1, minHeight:0, overflowY:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
        <FollowUpView
          token={token}
          formatDateTime={formatDateTime}
          formatDate={formatDate}
          leads={leads}
          initialData={allFollowups}
          onChanged={refreshAll}
          darkMode={darkMode} T={T}
        />
        </div>
      )}

      {/* QUOTATION VIEW */}
      {activeTab === 'quotations' && (
        <div style={{ flex:1, minHeight:0, overflowY:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
        <QuotationView
          token={token}
          leads={leads}
          formatDate={formatDate}
          initialData={allProposals}
          onChanged={refreshAll}
          darkMode={darkMode} T={T}
        />
        </div>
      )}

      {/* PERFORMANCE VIEW */}
      {activeTab === 'performance' && (
        <div style={{ flex:1, minHeight:0, overflowY:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
          <PerformanceView leads={leads} formatDate={formatDate} darkMode={darkMode} T={T} />
        </div>
      )}

      {/* ONLINE STORE: app store orders (each one is also a lead above), listed services, store settings */}
      {activeTab === 'store' && (
        <div style={{ flex:1, minHeight:0, overflowY:'auto', paddingRight:4 }}>
          <OnlineStoreSettings token={token} darkMode={darkMode} T={T} onLeadsChanged={() => { fetchLeads(); fetchStats(); }} />
        </div>
      )}
      </div>

      {/* Modals */}
      {showAddModal && canCreate && (
        <LeadFormModal token={token} onClose={() => setShowAddModal(false)}
          onSuccess={(msg) => { refreshAll(); setShowAddModal(false); if (msg) showToast(msg.text, msg.type); }}
          darkMode={darkMode} T={T} />
      )}
      {editLead && canEdit && (
        <LeadFormModal token={token} lead={editLead} onClose={() => setEditLead(null)}
          onSuccess={() => { fetchLeads(); fetchStats(); setEditLead(null); }}
          onDelete={() => { fetchLeads(); fetchStats(); setEditLead(null); }}
          darkMode={darkMode} T={T} />
      )}
      {selectedLead && (
        <LeadDetailModal lead={selectedLead} token={token} darkMode={darkMode} T={T}
          onClose={() => { setSelectedLead(null); refreshAll(); }}
          onSuccess={() => { refreshAll(); setSelectedLead(null); }}
          formatDateTime={formatDateTime}
          onFollowupChange={() => {
            fetch(`${BASE}/api/followups/all`, { headers: { 'Authorization': `Bearer ${token}` } })
              .then(r => r.json()).then(d => setAllFollowups(Array.isArray(d) ? d : [])).catch(console.error);
          }} />
      )}    </div>
  );
}

// FOLLOW-UP VIEW
function FollowUpView({ token, formatDateTime, formatDate, leads = [], initialData = [], onChanged, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff' };
  const [followups, setFollowups] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    // Show the parent's cached list instantly, then always re-read it, so a status changed
    // inside a lead (accepted then reverted to draft, follow-up done...) never stays stale here.
    if (initialData && initialData.length > 0) {
      setFollowups(initialData);
      setLoading(false);
    }
    {
      // Fallback: fetch directly if parent didn't pass data yet
      fetch(`${BASE}/api/followups/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.json()).then(data => {
        setFollowups(Array.isArray(data) ? data : []);
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [initialData]);

  const me = JSON.parse(localStorage.getItem('user') || '{}');
  const matchers = {
    all: () => true,
    pending: f => f.status === 'pending',
    overdue: f => isFollowupOverdue(f),
    today: f => isFollowupToday(f),
    mine: f => f.status === 'pending' && String(f.assigned_to) === String(me.id),
    done: f => f.status === 'done',
  };
  const filtered = followups.filter(matchers[filter] || matchers.all);
  const overdueCount = followups.filter(matchers.overdue).length;

return (
    <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, padding:16, minHeight:'100%', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap', marginBottom:10 }}>
        <h3 style={{ fontWeight:700, fontSize:13.5, color:T.text, margin:0 }}>Follow-ups <span style={{ fontWeight:500, color:T.text3 }}>({filtered.length})</span>{overdueCount > 0 && <span style={{ marginLeft:8, padding:'1px 8px', borderRadius:20, fontSize:10.5, fontWeight:700, background:'#fee2e2', color:'#b91c1c' }}>{overdueCount} overdue</span>}</h3>
        <div style={{ display:'flex', gap:4 }}>
          {['pending', 'overdue', 'today', 'mine', 'done', 'all'].map(s => (
            <button key={s} onClick={() => setFilter(s)} style={{
              padding:'4px 11px', borderRadius:7, fontSize:11.5, fontWeight:600,
              textTransform:'capitalize', cursor:'pointer', border:'none',
              background: filter===s ? '#0f1f45' : 'transparent',
              color: filter===s ? '#fff' : T.text2,
            }}>{s}</button>
          ))}
        </div>
      </div>
      {loading ? <p style={{ textAlign:'center', color:T.text3, padding:'24px 0' }}>Loading...</p> :
        filtered.length === 0 ? <p style={{ textAlign:'center', color:T.text3, padding:'24px 0' }}>No follow-ups found</p> : (
          <div style={{ border:`1px solid ${T.border}`, borderRadius:10, overflow:'hidden' }}>
            {filtered.map((f, idx) => {
              const lead = leads.find(l => String(l.id) === String(f.lead_id));
              const overdue = isFollowupOverdue(f);
              const done = f.status === 'done';
              return (
                <div key={f.id}
                  style={{ display:'grid', gridTemplateColumns:'22px minmax(150px,1.3fr) 138px minmax(100px,1.6fr) 120px 74px', alignItems:'center', gap:12, padding:'7px 12px', borderTop: idx ? `1px solid ${T.border}` : 'none', background: overdue && !done ? (darkMode?'rgba(239,68,68,0.06)':'#fff8f8') : 'transparent', opacity: done ? 0.7 : 1, fontSize:12 }}
                  onMouseEnter={e => { if (!(overdue && !done)) e.currentTarget.style.background = T.bg3; }}
                  onMouseLeave={e => { e.currentTarget.style.background = overdue && !done ? (darkMode?'rgba(239,68,68,0.06)':'#fff8f8') : 'transparent'; }}>
                  <span title={f.type} style={{ color:'#6366f1', display:'flex', alignItems:'center', justifyContent:'center', transform:'scale(0.8)' }}>{typeIcon(f.type)}</span>
                  <div style={{ minWidth:0, display:'flex', alignItems:'center', gap:6 }}>
                    <span title={f.lead_company ? `${f.lead_name} · ${f.lead_company}` : f.lead_name} style={{ fontSize:12.5, fontWeight:600, color:T.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{f.lead_name || 'Lead'}</span>
                    {lead ? (
                      <button onClick={() => setSelectedLead(lead)} style={{ flexShrink:0, padding:'0 7px', background: darkMode?'rgba(99,102,241,0.15)':'#eef2ff', color:'#6366f1', borderRadius:20, fontSize:10.5, fontWeight:600, border:'none', cursor:'pointer', lineHeight:'18px' }}>{lead.lead_id}</button>
                    ) : f.lead_id ? <span style={{ flexShrink:0, fontSize:10.5, color:T.text3 }}>#{f.lead_id}</span> : null}
                  </div>
                  <span style={{ color: overdue && !done ? '#b91c1c' : T.text2, fontWeight: overdue && !done ? 600 : 400, whiteSpace:'nowrap' }}>
                    <span style={{ textTransform:'capitalize' }}>{f.type}</span> · {formatDate(f.follow_up_date)}{f.follow_up_time && ` ${f.follow_up_time.slice(0, 5)}`}
                  </span>
                  <span title={f.notes || ''} style={{ color:T.text3, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{f.notes || '—'}</span>
                  <span title={`Assigned to ${f.assigned_to_name || '—'} · set by ${f.created_by_name || '—'}`} style={{ color:T.text2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{f.assigned_to_name || '—'}</span>
                  <span style={{ justifySelf:'end', padding:'1px 9px', borderRadius:20, fontSize:10.5, fontWeight:700, whiteSpace:'nowrap',
                    background: done ? (darkMode?'rgba(16,185,129,0.15)':'#f0fdf4') : overdue ? '#fee2e2' : (darkMode?'rgba(245,158,11,0.15)':'#fffbeb'),
                    color: done ? '#16a34a' : overdue ? '#b91c1c' : '#d97706' }}>
                    {done ? 'Done' : overdue ? 'Overdue' : 'Pending'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
    {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          token={token}
          darkMode={darkMode}
          T={T}
          initialTab="followup"
          onFollowupChange={() => {
            fetch(`${BASE}/api/followups/all`, { headers: { 'Authorization': `Bearer ${token}` } })
              .then(r => r.json()).then(d => setFollowups(Array.isArray(d) ? d : [])).catch(() => {});
          }}
          onClose={() => { setSelectedLead(null); if (onChanged) onChanged(); }}
          onSuccess={() => { setSelectedLead(null); if (onChanged) onChanged(); }}
          formatDateTime={(d) => new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
        />
      )}
    </div>
  );
}

// QUOTATION VIEW
function QuotationView({ token, leads, formatDate, initialData = [], onChanged, darkMode = false, T: theme }) {
  const [confirm, setConfirm] = useState(null);
  const showConfirm = (title, message, onConfirm, confirmLabel='Confirm', confirmColor='#ef4444') => setConfirm({ title, message, onConfirm, confirmLabel, confirmColor });
  const [toasts, showToast] = useToasts();
  const { canEdit, canDelete, deny } = useCrmAccess(showToast);
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff' };
  const [proposals, setProposals] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [openQuotationActionId, setOpenQuotationActionId] = useState(null);
  const [quotationMenuUp, setQuotationMenuUp] = useState(false); // flip the menu upward when there's no room below (last rows)

  // Helper: find the lead object from leads[] by proposal's lead_id
  const findLead = (p) => leads.find(l => String(l.id) === String(p.lead_id));
  const [gstRegQ, setGstRegQ] = useState(true);
  useEffect(() => {
    fetch(`${BASE}/api/invoices/settings/config`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setGstRegQ((d || {}).is_gst_registered !== false)).catch(() => {});
  }, [token]);

  useEffect(() => {
    // Show the parent's cached list instantly, then always re-read it, so a status changed
    // inside a lead (accepted then reverted to draft, follow-up done...) never stays stale here.
    if (initialData && initialData.length > 0) {
      setProposals(initialData);
      setLoading(false);
    }
    {
      fetch(`${BASE}/api/proposals/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.json()).then(data => {
        setProposals(Array.isArray(data) ? data : []);
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [initialData]);

  const filtered = proposals.filter(p => filter === 'all' || p.status === filter);

  const statusColor = (s) => ({
    draft: 'bg-gray-100 text-gray-700',
    sent: 'bg-blue-100 text-blue-700',
    accepted: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700'
  }[s] || 'bg-gray-100 text-gray-700');

  const handleQuotationStatusChange = async (proposal, status) => {
  if (!canEdit) { deny('edit'); return; }
  try {
    const res = await fetch(`${BASE}/api/proposals/${proposal.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({
        ...proposal,
        status,
        title: proposal.title || '',
        amount: proposal.amount || 0,
        content: proposal.content || ''
      })
    });

    const data = await res.json();

    if (!res.ok) {
      showToast(data.message || 'Could not update quotation status', 'error');
      return;
    }

    setProposals(prev => prev.map(p => p.id === proposal.id ? { ...p, status } : p));
    setOpenQuotationActionId(null);
    if (onChanged) onChanged(); // accepting wins the lead — stats and lists move with it
    if (status === 'accepted') {
      showToast(data.won_client ? (data.won_client.existing ? `Quotation accepted — lead won against existing client "${data.won_client.client_name}".` : `Quotation accepted — lead won and client "${data.won_client.client_name}" created.`) : 'Quotation accepted — lead marked as Won.');
      if (data.created_task_ids && data.created_task_ids.length) {
        setTimeout(() => showToast(`${data.created_task_ids.length} task${data.created_task_ids.length > 1 ? 's' : ''} created for the accepted service${data.created_task_ids.length > 1 ? 's' : ''}.`), 400);
      }
    }
  } catch (e) {
    showToast('Server error while updating quotation', 'error');
  }
};

const handleDeleteQuotation = (proposal) => {
  if (!canDelete) { deny('delete'); return; }
  showConfirm('Delete Quotation', `Delete quotation ${proposal.quote_number || ''}? This cannot be undone.`, async () => {
  try {
    const res = await fetch(`${BASE}/api/proposals/${proposal.id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });

    const data = await res.json();

    if (!res.ok) {
      showToast(data.message || 'Could not delete quotation', 'error');
      return;
    }

    setProposals(prev => prev.filter(p => p.id !== proposal.id));
    setOpenQuotationActionId(null);
    if (onChanged) onChanged();
  } catch (e) {
    showToast('Server error while deleting quotation', 'error');
  }
  });
};

const handleConvertQuotationToInvoice = async (proposal) => {
  if (!canEdit) { deny('edit'); return; }
  const lead = findLead(proposal);

  if (!lead) {
    showToast('Lead not found for this quotation', 'error');
    return;
  }

  try {
    const client = await getClientForLead(lead, token);

    if (!client?.id) {
      showToast('Could not find or create client from this lead', 'error');
      return;
    }

    localStorage.setItem('pendingInvoice', JSON.stringify({
      client_id: client.id,
      quotation_id: proposal.id,
      remarks: `Converted from Quotation ${proposal.quote_number || ''}. Lead Reference: ${lead.lead_id || lead.id}`,
      items: buildInvoiceItemsFromProposal(proposal),
      task_ids: proposal.task_ids || [],
    }));

    setOpenQuotationActionId(null);
    window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page: 'finance' } }));
  } catch (e) {
    showToast('Server error while converting quotation to invoice', 'error');
  }
};

  const qStatusMeta = {
    draft:    { bg: darkMode?'rgba(100,116,139,0.15)':'#f1f5f9', color:'#64748b' },
    sent:     { bg: darkMode?'rgba(59,130,246,0.15)' :'#eff6ff',  color:'#3b82f6' },
    accepted: { bg: darkMode?'rgba(16,185,129,0.15)' :'#f0fdf4',  color:'#16a34a' },
    rejected: { bg: darkMode?'rgba(239,68,68,0.15)'  :'#fef2f2',  color:'#dc2626' },
  };

  return (
    <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, padding:24, minHeight:'100%', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
      <ConfirmModal open={!!confirm} title={confirm?.title||''} message={confirm?.message||''} onConfirm={()=>{confirm?.onConfirm();setConfirm(null);}} onCancel={()=>setConfirm(null)} confirmLabel={confirm?.confirmLabel||'Confirm'} confirmColor={confirm?.confirmColor||'#ef4444'} />
      <ToastStack toasts={toasts} />
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <h3 style={{ fontWeight:700, fontSize:14, color:T.text, margin:0 }}>All Quotations</h3>
        <div style={{ display:'flex', gap:6 }}>
          {['all', 'draft', 'sent', 'accepted', 'rejected'].map(s => (
            <button key={s} onClick={() => setFilter(s)} style={{
              padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:600,
              textTransform:'capitalize', cursor:'pointer', border:'none',
              background: filter===s ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : T.bg3,
              color: filter===s ? '#fff' : T.text2,
              boxShadow: filter===s ? '0 3px 8px rgba(15,31,69,0.2)' : 'none',
            }}>{s}</button>
          ))}
        </div>
      </div>
      {loading ? <p style={{ textAlign:'center', color:T.text3, padding:'32px 0' }}>Loading...</p> :
        filtered.length === 0 ? <p style={{ textAlign:'center', color:T.text3, padding:'32px 0' }}>No quotations found</p> : (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
              <tr>
                {['Quote No.', 'Lead ID', 'Client', 'Title', 'Amount', 'Status', 'Created', 'Actions'].map(h => (
                  <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, idx) => (
                <tr key={p.id} style={{ borderBottom:`1px solid ${T.border}`, background: darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff'), transition:'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = darkMode?'#1e3a5f':'#f0f4ff'}
                  onMouseLeave={e => e.currentTarget.style.background = darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff')}>
                  <td style={{ padding:'10px 16px' }}>
                    <button onClick={() => { const l=findLead(p); if(l) setSelectedLead({...l,_openTab:'quotation'}); }}
                      style={{ color:'#3b82f6', fontWeight:600, background:'none', border:'none', cursor:'pointer', fontSize:13 }}>
                      #{p.quote_number}
                    </button>
                  </td>
                  <td style={{ padding:'10px 16px' }}>
                    {findLead(p)
                      ? <button onClick={() => setSelectedLead(findLead(p))} style={{ color:'#6366f1', fontWeight:600, background:'none', border:'none', cursor:'pointer', fontSize:12 }}>{findLead(p).lead_id}</button>
                      : <span style={{ color:T.text3, fontSize:12 }}>{p.lead_id||'—'}</span>}
                  </td>
                  <td style={{ padding:'10px 16px', fontSize:13, color:T.text2 }}>{p.lead_company||p.lead_name}</td>
                  <td style={{ padding:'10px 16px', fontSize:13, color:T.text2 }}>{p.title}</td>
                  <td style={{ padding:'10px 16px', fontSize:13, fontWeight:600, color:'#16a34a' }}>
                    {(() => { const t = quoteTotals(itemsFromProposal(p), gstRegQ).total; return t ? `₹${t.toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '-'; })()}
                  </td>
                  <td style={{ padding:'10px 16px' }}>
                    <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600, textTransform:'capitalize', background:(qStatusMeta[p.status]||qStatusMeta.draft).bg, color:(qStatusMeta[p.status]||qStatusMeta.draft).color }}>
                      {p.status}
                    </span>
                  </td>
                  <td style={{ padding:'10px 16px', fontSize:12, color:T.text3 }}>{formatDate(p.created_at)}</td>
                  <td style={{ padding:'10px 16px', position:'relative' }}>
                    {(canEdit || canDelete) && <button onClick={(e) => { setQuotationMenuUp(window.innerHeight - e.currentTarget.getBoundingClientRect().bottom < 320); setOpenQuotationActionId(openQuotationActionId===p.id?null:p.id); }}
                      style={{ width:32, height:32, borderRadius:8, border:'none', background:'none', color:T.text2, fontSize:18, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                      onMouseLeave={e => e.currentTarget.style.background='none'}>⋮</button>}
                    {openQuotationActionId===p.id && (
                      <div onMouseLeave={() => setOpenQuotationActionId(null)}
                        style={{ position:'absolute', right:16, ...(quotationMenuUp ? { bottom:44 } : { top:44 }), background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 8px 24px rgba(15,31,69,0.12)', zIndex:50, width:240, padding:14 }}>
                        {canEdit && ['draft','sent'].includes(p.status||'draft') && (
                          <div style={{ display:'flex', gap:6, marginBottom:10 }}>
                            <button onClick={() => handleQuotationStatusChange(p, 'accepted')}
                              style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'7px 0', borderRadius:8, border:'1px solid #bbf7d0', background:darkMode?'rgba(16,185,129,0.12)':'#f0fdf4', color:'#16a34a', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                              <IconCheck /> Accept
                            </button>
                            <button onClick={() => handleQuotationStatusChange(p, 'rejected')}
                              style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'7px 0', borderRadius:8, border:'1px solid #fecaca', background:darkMode?'rgba(239,68,68,0.12)':'#fef2f2', color:'#dc2626', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                              <IconX /> Reject
                            </button>
                          </div>
                        )}
                        {p.converted_invoice_id ? (
                          <p style={{ fontSize:12, color:'#16a34a', fontWeight:600, marginBottom:10 }}>✓ Invoice already created — locked</p>
                        ) : canEdit && (
                          <>
                            <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>Change Status</label>
                            <select value={p.status||'draft'} onChange={e => handleQuotationStatusChange(p,e.target.value)}
                              style={{ width:'100%', border:`1px solid ${T.border2}`, borderRadius:8, padding:'7px 10px', fontSize:13, background:T.inputBg, color:T.text, marginBottom:10, outline:'none' }}>
                              {['draft','sent','accepted','rejected'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                            </select>
                          </>
                        )}
                        {canEdit && p.status==='accepted' && !p.converted_invoice_id && (
                          <button onClick={() => handleConvertQuotationToInvoice(p)}
                            style={{ width:'100%', textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', background:'none', color:'#16a34a', fontSize:13, fontWeight:600, cursor:'pointer' }}
                            onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(16,185,129,0.15)':'#f0fdf4'}
                            onMouseLeave={e => e.currentTarget.style.background='none'}>
                            Convert to Invoice
                          </button>
                        )}
                        {canDelete && <button onClick={() => handleDeleteQuotation(p)}
                          style={{ width:'100%', textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', background:'none', color:'#ef4444', fontSize:13, fontWeight:600, cursor:'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.15)':'#fef2f2'}
                          onMouseLeave={e => e.currentTarget.style.background='none'}>
                          Delete Quotation
                        </button>}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    {/* Lead Detail popup triggered from Quote No. or Lead ID click */}
      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          token={token}
          darkMode={darkMode}
          T={T}
          onClose={() => { setSelectedLead(null); if (onChanged) onChanged(); }}
          onSuccess={() => { setSelectedLead(null); if (onChanged) onChanged(); }}
          formatDateTime={(d) => new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
          initialTab={selectedLead._openTab || 'details'}
        />
      )}
    </div>
  );
}

// PERFORMANCE VIEW
function PerformanceView({ leads, formatDate, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9' };
  const [expandedMember, setExpandedMember] = useState(null); // ← accordion state

  const userStats = leads.reduce((acc, lead) => {
    const name = lead.assigned_name || 'Unassigned';
    if (!acc[name]) acc[name] = { name, total: 0, contacted: 0, proposal: 0, won: 0, lost: 0, new: 0, negotiation: 0 };
    acc[name].total++;
    acc[name][lead.stage] = (acc[name][lead.stage] || 0) + 1;
    return acc;
  }, {});

  const stats = Object.values(userStats);

  // Toggle: click same member again → collapse; click different → switch
  const handleMemberClick = (name) => {
    setExpandedMember(prev => prev === name ? null : name);
  };

  const memberLeads = expandedMember
    ? leads.filter(l => (l.assigned_name || 'Unassigned') === expandedMember)
    : [];

  const stageChip = (stage) => ({
    new:         'bg-blue-100 text-blue-700',
    contacted:   'bg-yellow-100 text-yellow-700',
    proposal:    'bg-purple-100 text-purple-700',
    negotiation: 'bg-orange-100 text-orange-700',
    won:         'bg-green-100 text-green-700',
    lost:        'bg-red-100 text-red-700',
    not_relevant:'bg-slate-100 text-slate-600',
  }[stage] || 'bg-gray-100 text-gray-600');

  const stageChipDark = (stage) => ({
    new:         { bg: darkMode?'rgba(99,102,241,0.15)' :'#eef2ff',  color:'#6366f1' },
    contacted:   { bg: darkMode?'rgba(245,158,11,0.15)' :'#fffbeb',  color:'#d97706' },
    proposal:    { bg: darkMode?'rgba(139,92,246,0.15)' :'#f5f3ff',  color:'#7c3aed' },
    negotiation: { bg: darkMode?'rgba(249,115,22,0.15)' :'#fff7ed',  color:'#ea580c' },
    won:         { bg: darkMode?'rgba(16,185,129,0.15)' :'#f0fdf4',  color:'#16a34a' },
    lost:        { bg: darkMode?'rgba(239,68,68,0.15)'  :'#fef2f2',  color:'#dc2626' },
    not_relevant:{ bg: darkMode?'rgba(100,116,139,0.15)':'#f1f5f9',  color:'#64748b' },
  }[stage] || { bg:T.bg3, color:T.text3 });

  return (
    <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, padding:24, minHeight:'100%', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
      <h3 style={{ fontWeight:700, fontSize:14, color:T.text, margin:'0 0 4px' }}>Team Performance</h3>
      <p style={{ fontSize:11, color:T.text3, margin:'0 0 16px' }}>💡 Click on a team member to see their leads</p>
      {stats.length === 0 ? (
        <p style={{ textAlign:'center', color:T.text3, padding:'32px 0' }}>No data available</p>
      ) : (
        <>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
              <tr>
                {['Team Member','Total Leads','New','Contacted','Proposal','Won','Lost','Conversion Rate'].map(h => (
                  <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stats.map((s, i) => (
                <tr key={i} onClick={() => handleMemberClick(s.name)} style={{
                  borderBottom:`1px solid ${T.border}`, cursor:'pointer', transition:'background 0.15s',
                  background: expandedMember===s.name
                    ? (darkMode?'rgba(99,102,241,0.12)':'#eef2ff')
                    : (darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff')),
                }}
                onMouseEnter={e => { if(expandedMember!==s.name) e.currentTarget.style.background = darkMode?'#1e3a5f':'#f0f4ff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = expandedMember===s.name?(darkMode?'rgba(99,102,241,0.12)':'#eef2ff'):(darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff')); }}>
                  <td style={{ padding:'10px 16px', fontSize:13, fontWeight:600 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, color:T.text }}>
                      <span style={{ transform: expandedMember===s.name?'rotate(90deg)':'rotate(0deg)', display:'inline-block', transition:'transform 0.2s', flexShrink:0 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                      </span>
                      {s.name}
                    </div>
                  </td>
                  <td style={{ padding:'10px 16px', fontSize:13, fontWeight:700, color:T.text }}>{s.total}</td>
                  {[
                    { val:s.new||0,       stage:'new'         },
                    { val:s.contacted||0, stage:'contacted'   },
                    { val:s.proposal||0,  stage:'proposal'    },
                    { val:s.won||0,       stage:'won'         },
                    { val:s.lost||0,      stage:'lost'        },
                  ].map(({ val, stage }) => {
                    const sc = stageChipDark(stage);
                    return (
                      <td key={stage} style={{ padding:'10px 16px' }}>
                        <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600, background:sc.bg, color:sc.color }}>{val}</span>
                      </td>
                    );
                  })}
                  <td style={{ padding:'10px 16px', fontSize:13, fontWeight:600, color:'#3b82f6' }}>
                    {s.total > 0 ? `${((s.won||0)/s.total*100).toFixed(1)}%` : '0%'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Accordion */}
          {expandedMember && (
            <div style={{ marginTop:16, border:`1px solid ${darkMode?'rgba(99,102,241,0.3)':'#c7d2fe'}`, borderRadius:12, overflow:'hidden' }}>
              <div style={{ background: darkMode?'rgba(99,102,241,0.12)':'#eef2ff', padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <p style={{ fontSize:13, fontWeight:700, color: darkMode?'#a5b4fc':'#4338ca', margin:0 }}>
                  👤 {expandedMember} — {memberLeads.length} Lead{memberLeads.length!==1?'s':''}
                </p>
                <button onClick={() => setExpandedMember(null)} style={{ background:'none', border:'none', color: darkMode?'#818cf8':'#6366f1', fontSize:18, cursor:'pointer', fontWeight:300 }}>✕</button>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                    <tr>
                      {['Lead ID','Business Name','Contact Person','Phone','Stage','Status','Source','Created'].map(h => (
                        <th key={h} style={{ padding:'8px 16px', textAlign:'left', fontSize:10, fontWeight:700, color:'#fff', textTransform:'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {memberLeads.length===0 ? (
                      <tr><td colSpan="8" style={{ padding:'24px 16px', textAlign:'center', color:T.text3 }}>No leads found</td></tr>
                    ) : memberLeads.map((l, idx) => {
                      const sc = stageChipDark(l.stage);
                      return (
                        <tr key={l.id} style={{ borderBottom:`1px solid ${T.border}`, background: darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff') }}
                          onMouseEnter={e => e.currentTarget.style.background = darkMode?'#1e3a5f':'#f0f4ff'}
                          onMouseLeave={e => e.currentTarget.style.background = darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff')}>
                          <td style={{ padding:'8px 16px', fontSize:12, fontWeight:600, color:'#6366f1' }}>{l.lead_id}</td>
                          <td style={{ padding:'8px 16px', fontSize:12, fontWeight:500, color:T.text }}>{l.company||'—'}</td>
                          <td style={{ padding:'8px 16px', fontSize:12, color:T.text2 }}>{l.name}</td>
                          <td style={{ padding:'8px 16px', fontSize:12, color:T.text3 }}>{l.phone||'—'}</td>
                          <td style={{ padding:'8px 16px' }}><span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:600, background:sc.bg, color:sc.color, textTransform:'capitalize' }}>{l.stage}</span></td>
                          <td style={{ padding:'8px 16px' }}><span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:600, background: l.status==='active'?(darkMode?'rgba(16,185,129,0.15)':'#f0fdf4'):(darkMode?'rgba(239,68,68,0.15)':'#fef2f2'), color: l.status==='active'?'#16a34a':'#dc2626' }}>{l.status}</span></td>
                          <td style={{ padding:'8px 16px', fontSize:12, color:T.text3, textTransform:'capitalize' }}>{l.source?.replace('_',' ')||'—'}</td>
                          <td style={{ padding:'8px 16px', fontSize:12, color:T.text3 }}>{formatDate(l.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    {/* Referral Partner Performance Link */}
      <div style={{ marginTop:16, padding:'14px 20px', background:T.bg3, border:`1px solid ${T.border}`, borderRadius:12, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>Referral Partner Performance</p>
          <p style={{ fontSize:11, color:T.text3, margin:'3px 0 0' }}>View partner leaderboard, commission earned and lead conversion</p>
        </div>
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page: 'referrals' } }))}
          style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer', flexShrink:0 }}>
          View Referral Performance
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
        </button>
      </div>
    </div>
  );
}

// LEAD FORM MODAL
function LeadFormModal({ token, lead, onClose, onSuccess, onDelete, darkMode = false, T: theme }) {
  const [confirm, setConfirm] = useState(null);
  const showConfirm = (title, message, onConfirm, confirmLabel='Confirm', confirmColor='#ef4444') => setConfirm({ title, message, onConfirm, confirmLabel, confirmColor });
  const [toasts, showToast] = useToasts();
  const { canCreate, canEdit, canDelete, deny } = useCrmAccess(showToast);
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [leadStages, setLeadStages] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const stagesForUI = leadStages.length ? leadStages : LEAD_STAGES;
  const fetchLeadStages = () => fetch(`${BASE}/api/leads/stages/all`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()).then(d => setLeadStages(Array.isArray(d) ? d : [])).catch(() => {});
  const fetchLeadSources = () => fetch(`${BASE}/api/leads/sources/all`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()).then(d => setLeadSources(Array.isArray(d) ? d : [])).catch(() => {});
  const createLeadStage = async (name) => {
    if (!canCreate) { deny('create'); return null; }
    const res = await fetch(`${BASE}/api/leads/stages`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ name }) });
    const d = await res.json();
    if (!res.ok) { showToast(d.message || 'Could not add stage', 'error'); return null; }
    fetchLeadStages();
    return d;
  };
  const createLeadSource = async (name) => {
    if (!canCreate) { deny('create'); return null; }
    const res = await fetch(`${BASE}/api/leads/sources`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ name }) });
    const d = await res.json();
    if (!res.ok) { showToast(d.message || 'Could not add source', 'error'); return null; }
    fetchLeadSources();
    return d;
  };
  const [selectedServices, setSelectedServices] = useState(
    lead?.services ? lead.services.split(',').map(s => s.trim()).filter(Boolean) : []
  );
  const [serviceSearch, setServiceSearch] = useState('');
  const [showServicePicker, setShowServicePicker] = useState(false);
  // Step 3 — optionally raise a Draft quotation right now. Rates start from the
  // Services Master default but can be overridden per line (or filled in when missing).
  // Referred by a referral partner (picked from the list) or by any other person (typed name)
  const [refMode, setRefMode] = useState(lead && !lead.referred_by && lead.reference_name ? 'person' : 'partner');
  const [quoteOn, setQuoteOn] = useState(false);
  const [qRate, setQRate] = useState({});
  const [qQty, setQQty] = useState({});
  // New leads get a first follow-up scheduled by default (tomorrow 10:00) —
  // the single biggest fix for leads going cold because nobody booked the next call.
  const [firstFu, setFirstFu] = useState({ on: true, days: 1, time: '10:00', type: 'call' });
  const [form, setForm] = useState({
    name: lead?.name || '',
    company: lead?.company || '',
    phone: lead?.phone || '',
    email: lead?.email || '',
    source: lead?.source || 'referral',
    referred_by: lead?.referred_by || '',
    reference_name: lead?.reference_name || '',
    stage: lead?.stage || 'new',
    status: lead?.status || 'active',
    assigned_to: lead?.assigned_to || '',
    assignee_ids: Array.isArray(lead?.assignee_ids) && lead.assignee_ids.length ? lead.assignee_ids : (lead?.assigned_to ? [lead.assigned_to] : []),
    priority: lead?.priority || 'medium',
    expected_deal_value: lead?.expected_deal_value || '',
    lead_temperature: lead?.lead_temperature || 'warm',
    gst_type: lead?.gst_type || '',
    gstin: lead?.gstin || '',
    place_of_supply: lead?.place_of_supply || '',
    currency: lead?.currency || 'INR',
    notes: lead?.notes || '',
    lead_attachments: lead?.lead_attachments || [],
    client_id: lead?.client_id || null
  });
  // New customer vs. an EXISTING client (e.g. a quotation for an extra service).
  const [custMode, setCustMode] = useState('new');
  const [clients, setClients] = useState([]);
  const [existingClient, setExistingClient] = useState(null);
  useEffect(() => {
    if (lead) return;
    fetch(`${BASE}/api/clients`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setClients(Array.isArray(d) ? d : [])).catch(() => {});
  }, [token]);
  const pickExistingClient = async (c) => {
    setExistingClient(c);
    setForm(f => ({ ...f, client_id: c.id, name: c.contact_person || c.client_name || f.name, company: c.client_name || '', phone: normalizePhoneIN(c.mobile || ''), email: c.email || '', gstin: c.gstin || '', source: 'existing_client' }));
    // make sure the "Existing Client" source exists in this firm's list (no-op if it does)
    if (!leadSources.some(sv => sv.value === 'existing_client') && canCreate) await createLeadSource('Existing Client');
  };
  const switchCustMode = (m) => {
    setCustMode(m);
    if (m === 'new' && existingClient) {
      setExistingClient(null);
      setForm(f => ({ ...f, client_id: null, source: 'referral' }));
    }
  };

  useEffect(() => {
    fetch(`${BASE}/api/users`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(data => setUsers(Array.isArray(data) ? data : [])).catch(console.error);

    fetch(`${BASE}/api/services`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(data => setServices(Array.isArray(data) ? data : [])).catch(console.error);

    fetchLeadStages();
    fetchLeadSources();
  }, [token]);

  const groupedServices = services.reduce((acc, service) => {
    const category = service.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(service);
    return acc;
  }, {});

  const filteredGroupedServices = Object.entries(groupedServices).reduce((acc, [category, list]) => {
    const filtered = list.filter(s =>
      s.name?.toLowerCase().includes(serviceSearch.toLowerCase()) ||
      category.toLowerCase().includes(serviceSearch.toLowerCase())
    );
    if (filtered.length) acc[category] = filtered;
    return acc;
  }, {});

  const toggleService = (name) => {
    setSelectedServices(prev =>
      prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]
    );
  };

  const handleAttachmentPick = (type, file) => {
    if (!file) return;
    setForm(prev => ({
      ...prev,
      lead_attachments: [
        ...(Array.isArray(prev.lead_attachments) ? prev.lead_attachments : []),
        {
          type,
          file_name: file.name,
          file_size: file.size,
          file_type: file.type,
          status: 'selected'
        }
      ]
    }));
  };

  const removeAttachment = (index) => {
    setForm(prev => ({
      ...prev,
      lead_attachments: (prev.lead_attachments || []).filter((_, i) => i !== index)
    }));
  };

  // One save at a time: a second click while the first request is still in
  // flight used to POST the lead again (a duplicate lead).
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;

    if (lead ? !canEdit : !canCreate) { deny(lead ? 'edit' : 'create'); return; }

    if (selectedServices.length === 0) {
      showToast('Pick at least one service from the Services Master before saving this lead.', 'error');
      return;
    }

    if (form.source === 'referral' && !form.referred_by && !(form.reference_name || '').trim()) {
      showToast('Referral lead — pick the referral partner, or choose "Other person" and type who referred it.', 'error');
      return;
    }

    if (!lead && quoteOn) {
      const missing = selectedServices.find(nm => !(Number(rateFor(nm)) > 0));
      if (missing) { showToast(`Enter a rate for "${missing}" or switch the quotation off.`, 'error'); return; }
    }

    submittingRef.current = true; setSubmitting(true);
    try {
      const url = lead ? `${BASE}/api/leads/${lead.id}` : `${BASE}/api/leads`;
      const method = lead ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ...form,
          assigned_to: form.assignee_ids[0] || '',
          services: selectedServices.join(', '),
          expected_deal_value: Number(form.expected_deal_value || 0)
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Optional: spin up a Draft quotation right away, pre-filled from the
        // services just picked and their default Services Master rate — the
        // user finishes/sends it later from the Quotation tab. onSuccess()
        // below unmounts this modal, so any toast has to be handed up to the
        // parent CRM component rather than shown via our own local toast.
        let successMsg = null;
        if (!lead && quoteOn && data.id) {
          const items = selectedServices.map(name => {
            const svc = services.find(s => s.name === name);
            const rate = Number(rateFor(name)) || 0, quantity = Number(qtyFor(name)) || 1;
            return svc
              ? { service_id: svc.id, particular: svc.name, description: svc.invoice_description || svc.description || '', quantity, rate, discount_type: 'none', discount_value: 0 }
              : { service_id: null, particular: name, description: '', quantity, rate, discount_type: 'none', discount_value: 0 };
          });
          try {
            const qRes = await fetch(`${BASE}/api/proposals`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ lead_id: data.id, items, status: 'draft', notes: '', terms: '' })
            });
            successMsg = qRes.ok
              ? { text: 'Lead added — a draft quotation was created too. Finish it from the Quotation tab.' }
              : { text: 'Lead added, but the draft quotation could not be created.', type: 'error' };
          } catch (e) {
            successMsg = { text: 'Lead added, but the draft quotation could not be created.', type: 'error' };
          }
        }
        if (!lead && firstFu.on && data.id) {
          try {
            const meUser = JSON.parse(localStorage.getItem('user') || '{}');
            const fRes = await fetch(`${BASE}/api/followups`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ lead_id: data.id, follow_up_date: isoDateOffset(firstFu.days), follow_up_time: firstFu.time || '10:00', type: firstFu.type, notes: 'First follow-up', assigned_to: form.assignee_ids[0] || meUser.id, remind_before_minutes: 15 })
            });
            const fuText = fRes.ok ? 'First follow-up scheduled — reminders are on.' : 'Lead added, but the follow-up could not be scheduled — add it from the lead.';
            successMsg = { text: successMsg ? `${successMsg.text} ${fuText}` : (fRes.ok ? `Lead added. ${fuText}` : fuText), type: fRes.ok ? successMsg?.type : 'error' };
          } catch (e) { /* lead is saved; the follow-up can be added from the lead */ }
        }
        onSuccess(successMsg);
      } else {
        showToast(data.message || 'Could not save lead', 'error');
      }
    } catch (error) {
      showToast('Server error while saving lead', 'error');
      console.error(error);
    } finally {
      submittingRef.current = false; setSubmitting(false);
    }
  };

  const handleDelete = () => {
    if (!canDelete) { deny('delete'); return; }
    if (TERMINAL_STAGES.includes(lead?.stage)) {
      showToast(`${stagesForUI.find(s => s.value === lead.stage)?.name || 'This'} leads cannot be deleted.`, 'error');
      return;
    }

    showConfirm('Delete Lead', 'Are you sure you want to delete this lead? This cannot be undone.', async () => {
    try {
      const res = await fetch(`${BASE}/api/leads/${lead.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        if (onDelete) onDelete();
        else onSuccess();
      } else {
        showToast(data.message || 'Could not delete lead', 'error');
      }
    } catch (error) {
      showToast('Server error while deleting lead', 'error');
      console.error(error);
    }
    });
  };

  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const SL = ({ children }) => <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>{children}</label>;
  const svcOf = (name) => services.find(sv => sv.name === name);
  const rateFor = (name) => qRate[name] !== undefined ? qRate[name] : String(svcOf(name)?.default_amount || '');
  const qtyFor = (name) => qQty[name] !== undefined ? qQty[name] : '1';
  const quoteTotal = selectedServices.reduce((t, nm) => t + (Number(rateFor(nm)) || 0) * (Number(qtyFor(nm)) || 0), 0);
  const SC = ({ title, step }) => (
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
      {step && <span style={{ width:22, height:22, borderRadius:'50%', background:'#0f1f45', color:'#fff', fontSize:11.5, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{step}</span>}
      <p style={{ fontSize:12.5, fontWeight:700, color:T.text, textTransform:'uppercase', letterSpacing:'0.08em', margin:0 }}>{title}</p>
    </div>
  );

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:16 }}>
      <ConfirmModal open={!!confirm} title={confirm?.title||''} message={confirm?.message||''} onConfirm={()=>{confirm?.onConfirm();setConfirm(null);}} onCancel={()=>setConfirm(null)} confirmLabel={confirm?.confirmLabel||'Confirm'} confirmColor={confirm?.confirmColor||'#ef4444'} />
      <ToastStack toasts={toasts} />
      <div style={{ background:T.bg2, borderRadius:20, width:'90vw', maxWidth:1100, maxHeight:'92vh', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>

        {/* Header */}
        <div style={{ padding:'18px 24px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', background:T.bg2, flexShrink:0 }}>
          <div>
            <h2 style={{ fontSize:18, fontWeight:700, color:T.text, margin:0 }}>{lead ? 'Edit Lead' : 'Add New Lead'}</h2>
            <p style={{ fontSize:14, color:T.text3, marginTop:3 }}>CRM Lead Capture Form</p>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {lead && canDelete && !TERMINAL_STAGES.includes(lead.stage) && (
              <button type="button" onClick={handleDelete} style={{
                display:'flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:8,
                background:'linear-gradient(135deg,#dc2626,#b91c1c)', border:'none',
                color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer',
                boxShadow:'0 3px 10px rgba(220,38,38,0.3)',
              }}>
                <IconTrash />
                Delete
              </button>
            )}
            <button type="button" onClick={onClose} style={{ width:32, height:32, borderRadius:'50%', border:'none', background:T.bg3, color:T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ flex:1, minHeight:0, display:'flex', flexDirection:'column' }}>
          <div className="lead-form-scroll" style={{ flex:1, minHeight:0, overflowY:'auto', padding:'20px 24px', display:'flex', flexDirection:'column', gap:16, scrollbarWidth:'none', msOverflowStyle:'none' }}>

            {/* Basic Info */}
            <div style={{ border:`1px solid ${T.border}`, borderRadius:12, padding:18, background:T.bg3 }}>
              <SC step={1} title="Client" />
              {!lead && (
                <div style={{ marginBottom:14 }}>
                  <div style={{ display:'inline-flex', padding:3, borderRadius:10, background:T.bg2, border:`1px solid ${T.border2}`, marginBottom: custMode === 'existing' ? 12 : 0 }}>
                    {[['new', 'New customer'], ['existing', 'Existing client']].map(([k, label]) => (
                      <button key={k} type="button" onClick={() => switchCustMode(k)}
                        style={{ padding:'6px 14px', borderRadius:8, border:'none', fontSize:12.5, fontWeight:600, cursor:'pointer', background: custMode === k ? '#0f1f45' : 'transparent', color: custMode === k ? '#fff' : T.text2 }}>{label}</button>
                    ))}
                  </div>
                  {custMode === 'existing' && (
                    <ExistingClientPicker clients={clients} selected={existingClient} onPick={pickExistingClient} onClear={() => switchCustMode('new')} darkMode={darkMode} T={T} />
                  )}
                </div>
              )}
              {lead && lead.client_id && (
                <div style={{ marginBottom:14, padding:'9px 12px', borderRadius:9, background:'rgba(14,165,233,0.1)', color:'#0369a1', fontSize:12.5, fontWeight:600 }}>Raised for existing client: {lead.linked_client_name || `#${lead.client_id}`}{lead.linked_client_file_number ? ` (${lead.linked_client_file_number})` : ''}</div>
              )}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14 }}>
                {[
                  { label:'Contact Person *', field:'name', type:'text', req:true, ph:'Full name' },
                  { label:'Mobile Number *',  field:'phone',   type:'tel', req:true, ph:'Mobile number' },
                  { label:'Business Name',    field:'company', type:'text', ph:'Company / firm name' },
                  { label:'Email',            field:'email',   type:'email', ph:'Email address' },
                ].map(f => (
                  <div key={f.field}>
                    <SL>{f.label}</SL>
                    {f.field === 'phone' ? (
                      <div style={{ display:'flex', alignItems:'stretch' }}>
                        <span style={{ display:'flex', alignItems:'center', padding:'0 10px', border:`1px solid ${T.inputBorder}`, borderRight:'none', borderRadius:'9px 0 0 9px', background:T.bg2, color:T.text2, fontSize:13, fontWeight:600 }}>+91</span>
                        <input required type="tel" inputMode="tel" value={form.phone}
                          onChange={e => setForm({ ...form, phone: e.target.value })}
                          onBlur={e => setForm(fm => ({ ...fm, phone: normalizePhoneIN(e.target.value) }))}
                          placeholder="10-digit mobile" style={{ ...is, borderRadius:'0 9px 9px 0', flex:1, minWidth:0 }} />
                      </div>
                    ) : (
                    <input required={f.req} type={f.type} value={form[f.field]}
                      onChange={e => setForm({ ...form, [f.field]: e.target.value })}
                      placeholder={f.ph} style={is} />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Services — moved up right after Contact Person/Mobile, and made
                mandatory (see handleSubmit) since a lead can't be reliably
                won/quoted/tasked against "no service in particular". */}
            <div style={{ border:`1px solid ${T.border}`, borderRadius:12, padding:18, background:T.bg3 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                <SC step={2} title="Service Required *" />
                <span style={{ fontSize:11, color:T.text3 }}>{selectedServices.length} selected</span>
              </div>
              {selectedServices.length > 0 && (
                <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:12 }}>
                  {selectedServices.map(s => (
                    <span key={s} style={{ display:'inline-flex', alignItems:'center', gap:6, paddingLeft:12, paddingRight:8, paddingTop:6, paddingBottom:6, borderRadius:20, fontSize:12, fontWeight:600, border:`1px solid ${darkMode?'rgba(59,130,246,0.3)':'#bfdbfe'}`, background: darkMode?'rgba(59,130,246,0.12)':'#eff6ff', color: darkMode?'#93c5fd':'#2563eb' }}>
                      {s}
                      <button type="button" onClick={() => toggleService(s)} style={{ width:16, height:16, borderRadius:'50%', background: darkMode?'rgba(59,130,246,0.2)':'#bfdbfe', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={darkMode?'#93c5fd':'#2563eb'} strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </span>
                  ))}
                  <button type="button" onClick={() => setSelectedServices([])} style={{ fontSize:12, color:'#ef4444', padding:'4px 10px', borderRadius:20, border:'none', background:'none', cursor:'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.1)':'#fef2f2'}
                    onMouseLeave={e => e.currentTarget.style.background='none'}>
                    Clear all
                  </button>
                </div>
              )}
              <div style={{ position:'relative' }}>
                <button type="button" onClick={() => { setServiceSearch(''); setShowServicePicker(p => !p); }}
                  style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%', padding:'10px 14px', borderRadius:10, fontSize:13, fontWeight:500, cursor:'pointer', border:`1.5px solid ${showServicePicker?(darkMode?'rgba(59,130,246,0.5)':'#93c5fd'):T.border2}`, background: showServicePicker?(darkMode?'rgba(59,130,246,0.1)':'#eff6ff'):T.bg2, color: showServicePicker?(darkMode?'#93c5fd':'#2563eb'):T.text2, transition:'all 0.15s' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    {selectedServices.length === 0 ? 'Pick Services from Master' : `Add more services (${selectedServices.length} selected)`}
                  </div>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: showServicePicker?'rotate(180deg)':'rotate(0deg)', transition:'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
                </button>
                {showServicePicker && (
                  <div style={{ position:'absolute', left:0, top:'calc(100% + 4px)', background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:16, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.15)', zIndex:50, width:'100%' }}
                    onMouseLeave={() => setShowServicePicker(false)}>
                    <div style={{ padding:12, borderBottom:`1px solid ${T.border}` }}>
                      <div style={{ position:'relative' }}>
                        <svg style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)' }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                        <input type="text" placeholder="Search services..." value={serviceSearch} onChange={e => setServiceSearch(e.target.value)}
                          style={{ width:'100%', paddingLeft:32, paddingRight:12, paddingTop:8, paddingBottom:8, border:`1px solid ${T.inputBorder}`, borderRadius:8, fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' }} autoFocus />
                      </div>
                    </div>
                    <div style={{ maxHeight:256, overflowY:'auto' }}>
                      {Object.keys(filteredGroupedServices).length === 0 ? (
                        <p style={{ textAlign:'center', fontSize:12, color:T.text3, padding:'20px 0' }}>No services found</p>
                      ) : Object.entries(filteredGroupedServices).map(([category, list]) => (
                        <div key={category}>
                          <p style={{ fontSize:10, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.08em', padding:'8px 16px', background:T.bg3, margin:0 }}>{category}</p>
                          {list.map(service => {
                            const sel = selectedServices.includes(service.name);
                            return (
                              <button key={service.id} type="button" onClick={() => toggleService(service.name)}
                                style={{ width:'100%', textAlign:'left', padding:'10px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, border:'none', cursor:'pointer', background: sel?(darkMode?'rgba(59,130,246,0.1)':'#eff6ff'):T.bg2, transition:'background 0.1s' }}
                                onMouseEnter={e => { if(!sel) e.currentTarget.style.background=T.bg3; }}
                                onMouseLeave={e => { if(!sel) e.currentTarget.style.background=T.bg2; }}>
                                <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                                  <div style={{ width:16, height:16, borderRadius:4, border:`2px solid ${sel?'#3b82f6':T.border2}`, background: sel?'#3b82f6':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all 0.15s' }}>
                                    {sel && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                                  </div>
                                  <div style={{ minWidth:0 }}>
                                    <p style={{ fontSize:13, fontWeight:500, color: sel?(darkMode?'#93c5fd':'#2563eb'):T.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', margin:0 }}>{service.name}</p>
                                    {service.service_code && <p style={{ fontSize:11, color:T.text3, fontFamily:'monospace', margin:0 }}>{service.service_code}</p>}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                    <div style={{ padding:'10px 16px', borderTop:`1px solid ${T.border}`, background:T.bg3, borderRadius:'0 0 16px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <span style={{ fontSize:12, color:T.text3 }}>{selectedServices.length} service{selectedServices.length!==1?'s':''} selected</span>
                      <button type="button" onClick={() => setShowServicePicker(false)} style={{ fontSize:12, fontWeight:600, color:'#3b82f6', background:'none', border:'none', cursor:'pointer' }}>Done ✓</button>
                    </div>
                  </div>
                )}
              </div>

            </div>

            {/* Step 3 — Quotation (new leads only; existing leads use the Quotation tab) */}
            {!lead && (
              <div style={{ border:`1px solid ${T.border}`, borderRadius:12, padding:18, background:T.bg3 }}>
                <SC step={3} title="Quotation" />
                <div style={{ display:'flex', alignItems:'center', gap:14, flexWrap:'wrap', marginBottom: quoteOn ? 14 : 0 }}>
                  <span style={{ fontSize:13, color:T.text2 }}>Send a quotation to the client now?</span>
                  <div style={{ display:'inline-flex', padding:3, borderRadius:10, background:T.bg2, border:`1px solid ${T.border2}` }}>
                    {[[false, 'No, later'], [true, 'Yes, create now']].map(([v, label]) => (
                      <button key={label} type="button" onClick={() => setQuoteOn(v)}
                        style={{ padding:'6px 14px', borderRadius:8, border:'none', fontSize:12.5, fontWeight:600, cursor:'pointer', background: quoteOn === v ? (v ? '#16a34a' : '#0f1f45') : 'transparent', color: quoteOn === v ? '#fff' : T.text2 }}>{label}</button>
                    ))}
                  </div>
                  {!quoteOn && <span style={{ fontSize:11.5, color:T.text3 }}>You can also create it later from the lead's Quotation tab.</span>}
                </div>
                {quoteOn && (selectedServices.length === 0 ? (
                  <p style={{ fontSize:12.5, color:'#d97706', margin:0 }}>Pick the service(s) above first — they will appear here.</p>
                ) : (
                  <div style={{ border:`1px solid ${T.border}`, borderRadius:10, overflow:'hidden', background:T.bg2 }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 90px 150px 130px', gap:10, padding:'9px 14px', background:T.bg3, fontSize:10.5, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em' }}>
                      <span>Service</span><span>Qty</span><span>Rate (₹)</span><span style={{ textAlign:'right' }}>Amount</span>
                    </div>
                    {selectedServices.map(nm => {
                      const noDefault = !(Number(svcOf(nm)?.default_amount) > 0);
                      const amt = (Number(rateFor(nm)) || 0) * (Number(qtyFor(nm)) || 0);
                      return (
                        <div key={nm} style={{ display:'grid', gridTemplateColumns:'1fr 90px 150px 130px', gap:10, padding:'9px 14px', alignItems:'center', borderTop:`1px solid ${T.border}` }}>
                          <div style={{ minWidth:0 }}>
                            <div style={{ fontSize:13, fontWeight:600, color:T.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{nm}</div>
                            {noDefault && !(Number(rateFor(nm)) > 0) && <div style={{ fontSize:11, color:'#d97706' }}>No default rate in Services Master — enter one</div>}
                          </div>
                          <input type="number" min="1" step="1" value={qtyFor(nm)} onChange={e => setQQty({ ...qQty, [nm]: e.target.value })} style={{ ...is, padding:'7px 9px' }} />
                          <input type="number" min="0" step="0.01" value={rateFor(nm)} placeholder="0.00" onChange={e => setQRate({ ...qRate, [nm]: e.target.value })} style={{ ...is, padding:'7px 9px', borderColor: Number(rateFor(nm)) > 0 ? T.inputBorder : '#f59e0b' }} />
                          <span style={{ textAlign:'right', fontSize:13, fontWeight:600, color:T.text }}>{amt.toLocaleString('en-IN', { minimumFractionDigits:2, maximumFractionDigits:2 })}</span>
                        </div>
                      );
                    })}
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', background:T.bg3, borderTop:`1px solid ${T.border}` }}>
                      <span style={{ fontSize:11.5, color:T.text3 }}>Saved as a Draft (GST added when finalised) — finish & send from the Quotation tab.</span>
                      <span style={{ fontSize:13, fontWeight:700, color:T.text }}>Total ₹ {quoteTotal.toLocaleString('en-IN', { minimumFractionDigits:2, maximumFractionDigits:2 })}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Step 4 — First follow-up — new leads only */}
            {!lead && canCreate && (
              <div style={{ border:`1px solid ${T.border}`, borderRadius:12, padding:18, background:T.bg3 }}>
                <SC step={4} title="Schedule Follow-up" />
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', marginBottom: firstFu.on ? 14 : 0 }}>
                  <input type="checkbox" checked={firstFu.on} onChange={e => setFirstFu({ ...firstFu, on: e.target.checked })} style={{ width:15, height:15, accentColor:'#3b82f6' }} />
                  <span style={{ fontSize:13, fontWeight:700, color:T.text }}>Schedule the first follow-up now</span>
                  <span style={{ fontSize:11.5, color:T.text3 }}>— reminded at the time, and again if missed</span>
                </label>
                {firstFu.on && (
                  <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                    {FOLLOWUP_QUICK_DATES.map(([label, days]) => (
                      <button key={label} type="button" onClick={() => setFirstFu({ ...firstFu, days })}
                        style={{ padding:'6px 12px', borderRadius:20, fontSize:12, fontWeight:600, cursor:'pointer', border:`1px solid ${firstFu.days===days ? '#3b82f6' : T.border2}`, background: firstFu.days===days ? '#3b82f6' : T.bg2, color: firstFu.days===days ? '#fff' : T.text2 }}>{label}</button>
                    ))}
                    <input type="time" value={firstFu.time} onChange={e => setFirstFu({ ...firstFu, time: e.target.value })} style={{ ...is, width:120 }} />
                    <select value={firstFu.type} onChange={e => setFirstFu({ ...firstFu, type: e.target.value })} style={{ ...is, width:130 }}>
                      <option value="call">Call</option><option value="whatsapp">WhatsApp</option><option value="email">Email</option><option value="meeting">Meeting</option>
                    </select>
                    <span style={{ fontSize:11.5, color:T.text3 }}>Assigned to the lead owner (or you).</span>
                  </div>
                )}
              </div>
            )}

            {/* Lead Management */}
            <div style={{ border:`1px solid ${T.border}`, borderRadius:12, padding:18, background:T.bg3 }}>
              <SC step={5} title="About the Lead" />
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14 }}>
                <div>
                  <SL>Source</SL>
                  <PicklistDropdown items={leadSources} value={form.source} onChange={v => setForm({ ...form, source: v })}
                    onCreate={canCreate ? createLeadSource : undefined} placeholder="Select source" addLabel="Add new source" darkMode={darkMode} T={T} />
                </div>
                <div>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, marginBottom:5 }}>
                    <label style={{ fontSize:11, fontWeight:600, color:T.text3, textTransform:'uppercase', letterSpacing:'0.05em' }}>Referred By{form.source === 'referral' && <span style={{ color:'#ef4444' }}> *</span>}</label>
                    <div style={{ display:'inline-flex', padding:2, borderRadius:7, background:T.bg2, border:`1px solid ${T.border2}` }}>
                      {[['partner', 'Partner'], ['person', 'Other person']].map(([k, l]) => (
                        <button key={k} type="button"
                          onClick={() => { setRefMode(k); setForm(f => (k === 'partner' ? { ...f, reference_name: '' } : { ...f, referred_by: '' })); }}
                          style={{ padding:'2px 8px', borderRadius:5, border:'none', fontSize:10.5, fontWeight:600, cursor:'pointer', background: refMode === k ? '#0f1f45' : 'transparent', color: refMode === k ? '#fff' : T.text3 }}>{l}</button>
                      ))}
                    </div>
                  </div>
                  {refMode === 'partner' ? (
                    <ReferralPartnerSelect
                      value={form.referred_by}
                      onChange={val => setForm({ ...form, referred_by: val })}
                      token={token}
                      darkMode={darkMode}
                      T={T}
                    />
                  ) : (
                    <input type="text" maxLength={150} value={form.reference_name} onChange={e => setForm({ ...form, reference_name: e.target.value })}
                      placeholder="Name of the person who referred" style={is} />
                  )}
                </div>
                <div>
                  <SL>Stage</SL>
                  <PicklistDropdown items={stagesForUI} value={form.stage} onChange={v => setForm({ ...form, stage: v })}
                    onCreate={canCreate ? createLeadStage : undefined} placeholder="Select stage" addLabel="Add new stage" showColor darkMode={darkMode} T={T} />
                </div>
                <div>
                  <SL>Status</SL>
                  <PicklistDropdown items={[{ value:'active', name:'Active', color:'#16a34a' }, { value:'inactive', name:'Inactive', color:'#94a3b8' }]} value={form.status} onChange={v => setForm({ ...form, status: v })} showColor darkMode={darkMode} T={T} />
                </div>
              </div>
            </div>

            {/* Assignment */}
            <div style={{ border:`1px solid ${T.border}`, borderRadius:12, padding:18, background:T.bg3 }}>
              <SC title="Assignment" />
              <div style={{ marginBottom:14 }}>
                <SL>Assign To (one or more)</SL>
                <UserMultiSelect users={users} value={form.assignee_ids} onChange={ids => setForm({ ...form, assignee_ids: ids, assigned_to: ids[0] || '' })} darkMode={darkMode} T={T} />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14 }}>
                <div>
                  <SL>Priority</SL>
                  <PicklistDropdown items={[{ value:'low', name:'Low', color:'#94a3b8' }, { value:'medium', name:'Medium', color:'#3b82f6' }, { value:'high', name:'High', color:'#f59e0b' }, { value:'urgent', name:'Urgent', color:'#ef4444' }]} value={form.priority} onChange={v => setForm({ ...form, priority: v })} showColor darkMode={darkMode} T={T} />
                </div>
                <div>
                  <SL>Expected Deal Value</SL>
                  <input type="number" min="0" value={form.expected_deal_value} onChange={e => setForm({ ...form, expected_deal_value: e.target.value })} placeholder="0.00" style={is} />
                </div>
                <div>
                  <SL>Lead Temperature</SL>
                  <PicklistDropdown items={[{ value:'cold', name:'Cold', color:'#38bdf8' }, { value:'warm', name:'Warm', color:'#f59e0b' }, { value:'hot', name:'Hot', color:'#ef4444' }]} value={form.lead_temperature} onChange={v => setForm({ ...form, lead_temperature: v })} showColor darkMode={darkMode} T={T} />
                </div>
              </div>
            </div>

            {/* GST Details */}
            <div style={{ border:`1px solid ${T.border}`, borderRadius:12, padding:18, background:T.bg3 }}>
              <SC title="GST Details" />
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14 }}>
                <div>
                  <SL>GST Type</SL>
                  <select value={form.gst_type} onChange={e => setForm({ ...form, gst_type: e.target.value })} style={is}>
                    <option value="">Select</option>
                    <option value="registered">Registered</option><option value="unregistered">Unregistered</option>
                    <option value="composition">Composition</option><option value="consumer">Consumer</option>
                  </select>
                </div>
                <div>
                  <SL>GSTIN</SL>
                  <input type="text" value={form.gstin} onChange={e => setForm({ ...form, gstin: e.target.value.toUpperCase() })} placeholder="GSTIN" style={is} />
                </div>
                <div>
                  <SL>Place of Supply</SL>
                  <input type="text" value={form.place_of_supply} onChange={e => setForm({ ...form, place_of_supply: e.target.value })} placeholder="State / city" style={is} />
                </div>
                <div>
                  <SL>Currency</SL>
                  <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} style={is}>
                    <option value="INR">INR</option><option value="USD">USD</option><option value="EUR">EUR</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Notes + Attachments */}
            <div style={{ border:`1px solid ${T.border}`, borderRadius:12, padding:18, background:T.bg3 }}>
              <SC title="Notes + Attachments" />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                <div>
                  <SL>Notes</SL>
                  <textarea rows="7" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                    placeholder="Lead discussion notes, next step, special requirements..."
                    style={{ ...is, resize:'vertical' }} />
                </div>
                <div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
                    {[{key:'file',label:'File Upload'},{key:'visiting_card',label:'Visiting Card'},{key:'document',label:'Document'}].map(item => (
                      <label key={item.key} style={{ border:`2px dashed ${T.border2}`, borderRadius:12, padding:16, textAlign:'center', cursor:'pointer', display:'block', transition:'all 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor='#3b82f6'; e.currentTarget.style.background=darkMode?'rgba(59,130,246,0.05)':'#eff6ff'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor=T.border2; e.currentTarget.style.background='transparent'; }}>
                        <input type="file" style={{ display:'none' }} onChange={e => handleAttachmentPick(item.key, e.target.files?.[0])} />
                        <span style={{ display:'block', fontSize:22, marginBottom:6, color:T.text3 }}>+</span>
                        <span style={{ fontSize:11, fontWeight:600, color:T.text2 }}>{item.label}</span>
                      </label>
                    ))}
                  </div>
                  <div style={{ marginTop:14, display:'flex', flexDirection:'column', gap:6, maxHeight:112, overflowY:'auto' }}>
                    {(form.lead_attachments||[]).length === 0 ? (
                      <p style={{ fontSize:12, color:T.text3 }}>No attachments selected</p>
                    ) : form.lead_attachments.map((file, index) => (
                      <div key={index} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:T.bg2, border:`1px solid ${T.border}`, borderRadius:8, padding:'8px 12px' }}>
                        <div>
                          <p style={{ fontSize:12, fontWeight:500, color:T.text, margin:0 }}>{file.file_name}</p>
                          <p style={{ fontSize:10, color:T.text3, margin:0 }}>{file.type}</p>
                        </div>
                        <button type="button" onClick={() => removeAttachment(index)} style={{ fontSize:12, color:'#ef4444', background:'none', border:'none', cursor:'pointer' }}>Remove</button>
                      </div>
                    ))}
                  </div>
                  <p style={{ fontSize:11, color:'#d97706', marginTop:10 }}>Attachment metadata will save now. Cloudinary upload will be connected in document upload phase.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding:'14px 24px', borderTop:`1px solid ${T.border}`, background:T.bg2, display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
            <p style={{ fontSize:12, color:T.text3, margin:0 }}>
              {lead ? `Editing ${lead.lead_id||'lead'}` : 'New lead will be added to CRM pipeline'}
            </p>
            <div style={{ display:'flex', gap:10 }}>
              <button type="button" onClick={onClose} style={{ padding:'9px 20px', borderRadius:9, border:`1.5px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer' }}>
                Cancel
              </button>
              <button type="submit" disabled={submitting} style={{ padding:'9px 24px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.65 : 1, boxShadow:'0 4px 12px rgba(15,31,69,0.3)' }}>
                {submitting ? 'Saving…' : (lead ? 'Save Changes' : 'Add Lead')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// LEAD DETAIL MODAL
function LeadDetailModal({ lead, token, onClose, onSuccess, formatDateTime, onFollowupChange, initialTab, darkMode = false, T: theme }) {
  const DT = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [confirm, setConfirm] = useState(null);
  const showConfirm = (title, message, onConfirm, confirmLabel='Confirm', confirmColor='#ef4444') => setConfirm({ title, message, onConfirm, confirmLabel, confirmColor });
  const [toasts, showToast] = useToasts();
  const { canCreate, canEdit, canDelete, canExport, canFollowupDelete, deny } = useCrmAccess(showToast);
  const [activeTab, setActiveTab] = useState(initialTab || 'details');
  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState('');
  const [proposals, setProposals] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [leadSettings, setLeadSettings] = useState({});
  const gstReg = leadSettings.is_gst_registered !== false; // GST shows on quotations only for a GST-registered firm
  const [showFollowupForm, setShowFollowupForm] = useState(false);
  const [showProposalForm, setShowProposalForm] = useState(false);
  const [editFollowup, setEditFollowup] = useState(null);
  const [editProposal, setEditProposal] = useState(null);
  const [users, setUsers] = useState([]);
  const me = JSON.parse(localStorage.getItem('user') || '{}');
  // New follow-ups default to the lead's owner (falls back to me) and a 15-min heads-up.
  const blankFollowup = () => ({ follow_up_date: '', follow_up_time: '', type: 'call', notes: '', assigned_to: lead.assigned_to || me.id || '', remind_before_minutes: 15 });
  const [followupForm, setFollowupForm] = useState(blankFollowup);
  const emptyQuotationItem = () => ({ service_id: null, particular: '', description: '', quantity: 1, rate: '', discount_type: 'none', discount_value: 0, charge_type: 'professional', gst_applies: true });
  const [proposalForm, setProposalForm] = useState({ items: [emptyQuotationItem()], status: 'draft', notes: '', terms: '' });
  const [services, setServices] = useState([]);
  const [servicePickerFor, setServicePickerFor] = useState(null); // item index whose picker is open
  const [serviceSearch, setServiceSearch] = useState('');
  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500";

  useEffect(() => {
    fetchFollowups();
    fetchProposals();
    fetchComments(); // comments never loaded on open before — the list always said "No comments yet"
    fetch(`${BASE}/api/invoices/settings/config`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(r => r.json()).then(d => setLeadSettings(d || {})).catch(() => {});
    fetch(`${BASE}/api/services`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(r => r.json()).then(d => setServices(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${BASE}/api/users`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setUsers(Array.isArray(d) ? d : [])).catch(() => {});
  }, [lead.id]);

  const updateQuotationItem = (index, field, value) => {
    setProposalForm(f => ({ ...f, items: f.items.map((it, i) => i === index ? { ...it, [field]: value } : it) }));
  };
  const addQuotationItem = () => setProposalForm(f => ({ ...f, items: [...f.items, emptyQuotationItem()] }));
  const removeQuotationItem = (index) => setProposalForm(f => ({ ...f, items: f.items.length > 1 ? f.items.filter((_, i) => i !== index) : f.items }));
  const pickServiceForItem = (index, svc) => {
    updateQuotationItem(index, 'service_id', svc.id);
    setProposalForm(f => ({ ...f, items: f.items.map((it, i) => i === index ? {
      ...it,
      service_id: svc.id,
      particular: svc.name,
      description: svc.invoice_description || svc.description || '',
      rate: svc.default_amount || 0,
    } : it) }));
    setServicePickerFor(null);
    setServiceSearch('');
  };
  const filteredServices = services.filter(s => !serviceSearch ||
    s.name?.toLowerCase().includes(serviceSearch.toLowerCase()) ||
    (s.service_code || '').toLowerCase().includes(serviceSearch.toLowerCase()));

  const fetchComments = async () => {
    try {
      const res = await fetch(`${BASE}/api/leads/${lead.id}/comments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setComments(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  const handleSendComment = async () => {
    if (!canEdit) { deny('edit'); return; }
    if (!comment.trim()) return;
    try {
      const res = await fetch(`${BASE}/api/leads/${lead.id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ comment })
      });
      if (res.ok) {
        setComment('');
        fetchComments();
      }
    } catch (e) { console.error(e); }
  };

  const fetchFollowups = async () => {
    try {
      const res = await fetch(`${BASE}/api/followups/${lead.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      setFollowups(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchProposals = async () => {
    try {
      const res = await fetch(`${BASE}/api/proposals/${lead.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      setProposals(await res.json());
    } catch (e) { console.error(e); }
  };

  const handleAddFollowup = async (e) => {
    e.preventDefault();
    if (editFollowup ? !canEdit : !canCreate) { deny(editFollowup ? 'edit' : 'create'); return; }
    try {
      const url = editFollowup ? `${BASE}/api/followups/${editFollowup.id}` : `${BASE}/api/followups`;
      const method = editFollowup ? 'PUT' : 'POST';
      const fres = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...followupForm, lead_id: lead.id })
      });
      if (!fres.ok) { const d = await fres.json().catch(() => ({})); showToast(d.message || 'Could not save follow-up', 'error'); return; }
      showToast(editFollowup ? 'Follow-up updated.' : 'Follow-up scheduled — the assigned user will be reminded.');
      setShowFollowupForm(false);
      setEditFollowup(null);
      setFollowupForm(blankFollowup());
      fetchFollowups();
      // Notify parent CRM to refresh allFollowups for real-time Follow-up View
      if (onFollowupChange) onFollowupChange();
    } catch (e) { console.error(e); }
  };

  const [savingProposal, setSavingProposal] = useState(false);
  const handleAddProposal = async (e) => {
    e.preventDefault();
    if (savingProposal) return;
    if (editProposal ? !canEdit : !canCreate) { deny(editProposal ? 'edit' : 'create'); return; }
    if (proposalForm.items.some(it => !it.particular)) {
      showToast('Pick a service for every line item.', 'error');
      return;
    }
    setSavingProposal(true);
    try {
      const url = editProposal ? `${BASE}/api/proposals/${editProposal.id}` : `${BASE}/api/proposals`;
      const method = editProposal ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...proposalForm, lead_id: lead.id })
      });
      if (!res.ok) { const d = await res.json(); showToast(d.message || 'Could not save quotation', 'error'); return; }
      setShowProposalForm(false);
      setEditProposal(null);
      setProposalForm({ items: [emptyQuotationItem()], status: 'draft', notes: '', terms: '', valid_till: '' });
      fetchProposals();
      showToast(editProposal ? 'Quotation updated.' : 'Quotation created.');
    } catch (e) { showToast('Server error while saving quotation', 'error'); }
    finally { setSavingProposal(false); }
  };

  const handleFollowupSnooze = async (id, body) => {
    if (!canEdit) { deny('edit'); return; }
    try {
      const res = await fetch(`${BASE}/api/followups/${id}/snooze`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); showToast(d.message || 'Could not snooze', 'error'); return; }
      showToast('Follow-up snoozed — you\'ll be reminded at the new time.');
      fetchFollowups();
      if (onFollowupChange) onFollowupChange();
    } catch (e) { showToast('Server error while snoozing', 'error'); }
  };

  const handleFollowupDelete = (f) => {
    if (!canFollowupDelete) { showToast("You don't have permission to delete follow-ups. Ask your admin.", 'error'); return; }
    showConfirm('Delete follow-up', 'Delete this follow-up? This cannot be undone.', async () => {
      try {
        const res = await fetch(`${BASE}/api/followups/${f.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
        if (!res.ok) { const d = await res.json().catch(() => ({})); showToast(d.message || 'Could not delete follow-up', 'error'); return; }
        showToast('Follow-up deleted.');
        fetchFollowups();
        if (onFollowupChange) onFollowupChange();
      } catch (e) { showToast('Server error while deleting', 'error'); }
    }, 'Delete');
  };

  const handleFollowupStatus = async (id, status) => {
    if (!canEdit) { deny('edit'); return; }
    try {
      const r = await fetch(`${BASE}/api/followups/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); showToast(d.message || 'Could not update follow-up', 'error'); return; }
      if (status === 'done') showToast('Follow-up marked done.');
      fetchFollowups();
      // Notify parent CRM to refresh Follow-up View in real time
      if (onFollowupChange) onFollowupChange();
    } catch (e) { console.error(e); }
  };

  const handleConvert = () => {
    if (!canEdit) { deny('edit'); return; }
    showConfirm('Convert to Client', 'Convert this lead to a client?', async () => {
      try {
        await fetch(`${BASE}/api/leads/${lead.id}/convert`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } });
        onSuccess();
      } catch (e) { console.error(e); }
    }, 'Convert', '#16a34a');
  };

  const handleLost = () => {
    if (!canEdit) { deny('edit'); return; }
    showConfirm('Mark as Lost', 'Mark this lead as lost? This will close the lead.', async () => {
      try {
        await fetch(`${BASE}/api/leads/${lead.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ ...lead, stage: 'lost' })
        });
        onSuccess();
      } catch (e) { console.error(e); }
    }, 'Mark Lost', '#f97316');
  };


  // Genuine vector-text PDF via pdfmake (see utils/pdfDocGen.js) — silent,
  // one-click download, no print dialog, no server dependency (works
  // identically on Hostinger shared hosting and localhost), and no
  // html2canvas rasterization — so no blurry text, no jagged small-box
  // borders, real selectable/copyable text, a fraction of the file size.
  const handleDownloadQuotation = async (proposal) => {
    if (!canExport) { deny('export'); return; }
    const filename = `Quotation-${proposal.quote_number || 'draft'}.pdf`;
    const s = leadSettings || {};
    const pdfItems = itemsFromProposal(proposal);
    const qtPdf = quoteTotals(pdfItems, s.is_gst_registered !== false);
    const pdfTotal = qtPdf.total;
    const pColor = s.primary_color || '#0f1f45';
    const quoteNo = proposal.quote_number || `${s.quotation_prefix || 'QT'}/26-27/001`;
    const validDays = parseInt(s.quotation_valid_days) || 30;
    const validTill = proposal.valid_till || new Date(Date.now() + validDays * 24 * 60 * 60 * 1000).toISOString();

    let qrDataUrl = '';
    if (s.upi_id && s.show_quotation_upi) {
      const upiString = `upi://pay?${new URLSearchParams({ pa: s.upi_id, pn: s.firm_name || 'ChanakyaX', am: String(pdfTotal || ''), cu: 'INR', tn: `Quotation ${quoteNo}` }).toString()}`;
      try { qrDataUrl = await QRCode.toDataURL(upiString, { width: 240, margin: 1 }); } catch (e) { console.error('QR generation failed:', e); }
    }
    const [logoDataUrl, signatureDataUrl] = await Promise.all([
      imageUrlToDataUrl(s.logo_url), imageUrlToDataUrl(s.signature_url),
    ]);

    try {
      const doc = buildQuotationDoc({
        quoteNo, date: proposal.created_at, validTill, pColor,
        firmName: s.firm_name, firmAddr: buildFirmAddress(s),
        firmMobile: s.firm_mobile, firmEmail: s.firm_email, logoDataUrl,
        clientName: lead.company || lead.name, contactName: lead.name, clientPhone: lead.phone, clientEmail: lead.email, clientGstin: lead.gstin,
        items: pdfItems.map(it => ({ particular: it.particular, description: it.description, amount: calcItemAmount(it), discount: calcItemDiscount(it), net: calcItemNet(it), govt: isGovt(it), passThrough: isPassThrough(it) })),
        gstRegistered: s.is_gst_registered !== false, gstAmount: qtPdf.gst, taxableTotal: qtPdf.taxable, passThroughTotal: qtPdf.passThrough,
        subtotal: pdfItems.reduce((sum, it) => sum + calcItemAmount(it), 0),
        discountTotal: pdfItems.reduce((sum, it) => sum + calcItemDiscount(it), 0),
        total: pdfTotal, notes: proposal.notes, terms: proposal.terms || s.quotation_terms || '',
        bankName: s.bank_name, bankAccount: s.bank_account, bankIfsc: s.bank_ifsc, bankBranch: s.bank_branch,
        upiId: s.upi_id && s.show_quotation_upi ? s.upi_id : '', qrDataUrl,
        signatureDataUrl, signatureName: s.signature_name,
      });
      await downloadPdfDoc(doc, filename);
    } catch (e) {
      showToast('Could not generate PDF: ' + e.message, 'error');
    }
  };

  // Marks a quotation "sent" without a separate manual edit step
  const markProposalSent = async (proposal) => {
    if (proposal.status !== 'draft') return;
    try {
      const res = await fetch(`${BASE}/api/proposals/${proposal.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...proposal, status: 'sent' })
      });
      if (res.ok) fetchProposals();
    } catch (e) { console.error(e); }
  };

  // Amount the client actually pays (GST included for a registered firm), signed with the firm's own name.
  const shareAmount = (proposal) => {
    const t = quoteTotals(itemsFromProposal(proposal), gstReg).total;
    return t ? Number(t).toLocaleString('en-IN', { maximumFractionDigits: 2 }) + (gstReg ? ' (incl. GST)' : '') : 'TBD';
  };
  const signOff = () => leadSettings.firm_name || 'Team';

  const handleSendEmail = (proposal) => {
    if (!canEdit) { deny('edit'); return; }
    const subject = `Quotation ${proposal.quote_number} - ${proposal.title}`;
    const body = `Dear ${lead.name},\n\nPlease find our quotation for ${proposal.title}.\n\nAmount: INR ${shareAmount(proposal)}\n\nRegards,\n${signOff()}`;
    window.open(`mailto:${lead.email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
    markProposalSent(proposal);
  };

  const handleSendWhatsApp = (proposal) => {
    if (!canEdit) { deny('edit'); return; }
    const message = `Hello ${lead.name},\n\nQuotation No: *${proposal.quote_number}*\nService: *${proposal.title}*\nAmount: *INR ${shareAmount(proposal)}*\n\nPlease confirm to proceed.\n\nRegards,\n${signOff()}`;
    const phone = lead.phone ? waNumber(lead.phone) : '';
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`);
    markProposalSent(proposal);
  };

  // One-click Accept/Reject instead of "go through edit"
  const handleQuotationDecision = async (proposal, status) => {
    if (!canEdit) { deny('edit'); return; }
    try {
      const res = await fetch(`${BASE}/api/proposals/${proposal.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...proposal, status })
      });
      const data = await res.json();
      if (!res.ok) { showToast(data.message || 'Could not update quotation', 'error'); return; }
      fetchProposals();
      if (status === 'accepted') {
        showToast(data.won_client ? (data.won_client.existing ? `Quotation accepted — lead won against existing client "${data.won_client.client_name}".` : `Quotation accepted — lead won and client "${data.won_client.client_name}" created.`) : 'Quotation accepted — lead marked as Won.');
        if (data.created_task_ids && data.created_task_ids.length) {
          setTimeout(() => showToast(`${data.created_task_ids.length} task${data.created_task_ids.length > 1 ? 's' : ''} created for the accepted service${data.created_task_ids.length > 1 ? 's' : ''}.`), 400);
        }
      } else if (status === 'draft' && proposal.status === 'accepted') {
        showToast('Quotation reverted to Draft — you can edit it again.');
      }
    } catch (e) { showToast('Server error while updating quotation', 'error'); }
  };

  const goToInvoice = async (proposal) => {
    if (!canEdit) { deny('edit'); return; }
    try {
      const client = await getClientForLead(lead, token);
      if (!client?.id) { showToast('Could not find or create client from this lead', 'error'); return; }
      localStorage.setItem('pendingInvoice', JSON.stringify({
        source: 'quotation',
        quotation_id: proposal.id,
        client_id: client.id,
        quote_number: proposal.quote_number,
        remarks: `From Quotation ${proposal.quote_number || ''}${proposal.title ? ': ' + proposal.title : ''}`,
        items: buildInvoiceItemsFromProposal(proposal),
        task_ids: proposal.task_ids || [],
      }));
      window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page: 'finance' } }));
    } catch (e) {
      showToast('Server error while preparing invoice', 'error');
    }
  };

  const proposalStatusColor = (status) => ({
    draft: 'bg-gray-100 text-gray-700', sent: 'bg-blue-100 text-blue-700',
    accepted: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700'
  }[status] || 'bg-gray-100 text-gray-700');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <ConfirmModal open={!!confirm} title={confirm?.title||''} message={confirm?.message||''} onConfirm={()=>{confirm?.onConfirm();setConfirm(null);}} onCancel={()=>setConfirm(null)} confirmLabel={confirm?.confirmLabel||'Confirm'} confirmColor={confirm?.confirmColor||'#ef4444'} />
      <ToastStack toasts={toasts} />
      <div className={`lead-view-noscroll ${darkMode ? 'lead-view-dark' : ''} bg-white rounded-2xl w-full ${activeTab === 'quotation' ? 'max-w-6xl' : 'max-w-4xl'} overflow-y-auto shadow-2xl`} style={{ maxHeight:'90vh', scrollbarWidth:'none', msOverflowStyle:'none' }}>
        <style>{`.lead-view-noscroll::-webkit-scrollbar{display:none;width:0;height:0}`}</style>
        {darkMode && <style>{`
          .lead-view-dark{color-scheme:dark}
          .lead-view-dark.bg-white,.lead-view-dark .bg-white{background-color:${DT.bg2}!important}
          .lead-view-dark .bg-gray-50,.lead-view-dark .bg-gray-100,.lead-view-dark .hover\\:bg-gray-50:hover,.lead-view-dark .hover\\:bg-gray-100:hover{background-color:${DT.bg3}!important}
          .lead-view-dark .text-gray-900,.lead-view-dark .text-gray-800,.lead-view-dark .text-gray-700{color:${DT.text}!important}
          .lead-view-dark .text-gray-600{color:${DT.text2}!important}
          .lead-view-dark .text-gray-500,.lead-view-dark .text-gray-400,.lead-view-dark .text-gray-300{color:${DT.text3}!important}
          .lead-view-dark .border,.lead-view-dark .border-b,.lead-view-dark .border-t,.lead-view-dark .border-gray-100,.lead-view-dark .border-gray-200,.lead-view-dark .border-gray-300{border-color:${DT.border2}!important}
          .lead-view-dark .divide-y>*{border-color:${DT.border}!important}
          .lead-view-dark input,.lead-view-dark textarea,.lead-view-dark select{background-color:${DT.inputBg}!important;color:${DT.text}!important;border-color:${DT.inputBorder}!important}
          .lead-view-dark input::placeholder,.lead-view-dark textarea::placeholder{color:${DT.text3}!important}
          .lead-view-dark .bg-blue-50{background-color:rgba(59,130,246,0.14)!important}
          .lead-view-dark .bg-green-50,.lead-view-dark .bg-green-100{background-color:rgba(16,185,129,0.16)!important}
          .lead-view-dark .bg-yellow-100,.lead-view-dark .bg-yellow-50{background-color:rgba(245,158,11,0.16)!important}
          .lead-view-dark .bg-red-100,.lead-view-dark .bg-red-50{background-color:rgba(239,68,68,0.16)!important}
          .lead-view-dark .text-blue-700,.lead-view-dark .text-blue-600{color:#93c5fd!important}
        `}</style>}
        <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold" style={{ color: DT.text, margin: 0 }}>View Lead</h2>
            <p style={{ fontSize: 12, color: DT.text3, margin: '2px 0 0' }}>{lead.lead_id}{lead.company ? ` · ${lead.company}` : lead.name ? ` · ${lead.name}` : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            {lead && canDelete && !TERMINAL_STAGES.includes(lead.stage) && (
              <button onClick={() => showConfirm('Delete Lead', 'Delete this lead? This cannot be undone.', async () => { try { const res = await fetch(`${BASE}/api/leads/${lead.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }); const data = await res.json(); if (res.ok) onSuccess(); else showToast(data.message || 'Could not delete lead', 'error'); } catch(e){ showToast('Server error while deleting lead', 'error'); } })}
                className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-xs font-medium hover:bg-red-50 flex items-center gap-1.5">
                <IconTrash /> Delete
              </button>
            )}
            <button onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition">
              <IconX />
            </button>
          </div>
        </div>

        <div className="flex gap-4 px-6 border-b overflow-x-auto">
          {['details', 'followup', 'quotation'].map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`py-3 text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'}`}>
              {tab === 'details' ? 'Detail Notes' : tab === 'followup' ? `Follow Up (${followups.length})` : `Quotation (${proposals.length})`}
            </button>
          ))}
        </div>

        <div className="p-6 flex flex-col">
          {activeTab === 'details' && (
            <div className="grid grid-cols-2 gap-6">
              <div>
                {(() => {
                  const svcList = (lead.services || '').split(',').map(x => x.trim()).filter(Boolean);
                  const Field = ({ label, value, wide }) => (
                    <div style={{ gridColumn: wide ? 'span 2' : 'auto', minWidth: 0 }}>
                      <p style={{ fontSize: 11, fontWeight: 600, color: DT.text3, textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>{label}</p>
                      <p style={{ fontSize: 14, fontWeight: 500, color: DT.text, margin: '6px 0 0', lineHeight: 1.45, wordBreak: 'break-word' }}>{value || '—'}</p>
                    </div>
                  );
                  const Card = ({ title, mb, children }) => (
                    <div style={{ background: DT.bg2, border: `1px solid ${DT.border}`, borderRadius: 14, boxShadow: darkMode ? 'none' : '0 1px 3px rgba(15,31,69,0.04)', marginBottom: mb ? 20 : 0 }}>
                      <div style={{ padding: '14px 24px', borderBottom: `1px solid ${DT.border}`, background: DT.bg3, borderRadius: '14px 14px 0 0' }}>
                        <h3 style={{ fontSize: 12, fontWeight: 700, color: DT.text, textTransform: 'uppercase', letterSpacing: '0.07em', margin: 0 }}>{title}</h3>
                      </div>
                      <div style={{ padding: '22px 24px 24px' }}>{children}</div>
                    </div>
                  );
                  return (
                    <>
                      {/* Services required — the first thing a reader should see */}
                      <div style={{ border: `1px solid ${darkMode ? 'rgba(59,130,246,0.35)' : '#bfdbfe'}`, background: darkMode ? 'rgba(59,130,246,0.08)' : '#f5f9ff', borderRadius: 14, padding: 16, marginBottom: 22 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                          <p style={{ fontSize: 12, fontWeight: 700, color: darkMode ? '#93c5fd' : '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.07em', margin: 0 }}>Services Required</p>
                          <span style={{ fontSize: 11, fontWeight: 700, color: darkMode ? '#93c5fd' : '#1d4ed8', background: darkMode ? 'rgba(59,130,246,0.2)' : '#dbeafe', borderRadius: 20, padding: '2px 10px' }}>{svcList.length}</span>
                        </div>
                        {svcList.length === 0 ? (
                          <p style={{ fontSize: 13, color: DT.text3, margin: 0 }}>No service selected for this lead yet.</p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {svcList.map((sv, i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, background: DT.bg2, border: `1px solid ${darkMode ? 'rgba(59,130,246,0.25)' : '#dbeafe'}`, borderRadius: 10, padding: '10px 12px' }}>
                                <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
                                <span style={{ fontSize: 14, fontWeight: 600, color: DT.text }}>{sv}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <Card title="Business Info" mb>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '22px 32px' }}>
                          <Field label="Lead ID" value={lead.lead_id} />
                          <Field label="Source" value={lead.source} />
                          <Field label="Business Name" value={lead.company} wide />
                          <Field label="Contact Person" value={lead.name} />
                          <Field label="Phone" value={formatPhone(lead.phone)} />
                          <Field label="Email" value={lead.email} wide />
                          <Field label="Referred By" value={lead.referred_by_name ? `${lead.referred_by_name} (${lead.referred_by})` : (lead.referred_by || (lead.reference_name ? `${lead.reference_name} (reference)` : ''))} wide />
                          <Field label="Assigned To" value={(Array.isArray(lead.assignees) && lead.assignees.length ? lead.assignees.map(a => a.name).join(', ') : lead.assigned_name)} wide />
                          {lead.client_id && <Field label="Existing Client" value={`${lead.linked_client_name || ''}${lead.linked_client_file_number ? ' (' + lead.linked_client_file_number + ')' : ''}`} wide />}
                        </div>
                      </Card>

                      <Card title="GST Details">
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '22px 32px' }}>
                          <Field label="GST Type" value={lead.gst_type} />
                          <Field label="GSTIN" value={lead.gstin} />
                          <Field label="Place of Supply" value={lead.place_of_supply} />
                          <Field label="Currency" value={lead.currency} />
                        </div>
                      </Card>
                    </>
                  );
                })()}
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Notes</h3>
                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600 mb-4 min-h-16">{lead.notes || 'No notes added.'}</div>
                <h3 className="font-semibold text-gray-700 mb-3">Comments</h3>
                <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
                  {comments.length === 0 ? (
                    <p className="text-xs text-gray-400">No comments yet.</p>
                  ) : (
                    comments.map((c) => (
                      <div key={c.id} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-blue-600">{c.user_name}</span>
                          <span className="text-xs text-gray-400">
                            {new Date(c.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })}
                          </span>
                        </div>
                        <p className="text-xs text-gray-700">{c.comment}</p>
                      </div>
                    ))
                  )}
                </div>
                {canEdit && <div className="border border-gray-200 rounded-lg p-3">
                  <textarea rows="3" placeholder="Add a comment..."
                    value={comment} onChange={(e) => setComment(e.target.value)}
                    className="w-full text-sm focus:outline-none resize-none" />
                  <div className="flex justify-end mt-2">
                    <button onClick={handleSendComment}
                      disabled={!comment.trim()}
                      className="px-4 py-1.5 rounded-lg text-white text-xs font-medium disabled:opacity-50"
                      style={{ background: '#0f1f45' }}>
                      Send
                    </button>
                  </div>
                </div>}
              </div>
            </div>
          )}

          {activeTab === 'followup' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-700">Follow Up Schedule</h3>
                {canCreate && <button onClick={() => { setShowFollowupForm(!showFollowupForm); setEditFollowup(null); setFollowupForm(blankFollowup()); }}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium"
                  style={showFollowupForm ? { background: 'transparent', color: '#64748b', border: '1px solid #e2e8f0' } : { background: '#0f1f45', color: '#fff' }}>
                  {showFollowupForm ? 'Cancel' : '+ Add Follow Up'}
                </button>}
              </div>
              {showFollowupForm && (
                <form onSubmit={handleAddFollowup} className="bg-gray-50 rounded-xl p-4 mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">{editFollowup ? 'Edit Follow Up' : 'New Follow Up'}</p>
                  <div className="flex flex-wrap items-center gap-1.5 mb-3">
                    <span className="text-xs text-gray-500 mr-1">Quick pick:</span>
                    {FOLLOWUP_QUICK_DATES.map(([label, days]) => {
                      const val = isoDateOffset(days);
                      const on = followupForm.follow_up_date === val;
                      return (
                        <button key={label} type="button" onClick={() => setFollowupForm({ ...followupForm, follow_up_date: val, follow_up_time: followupForm.follow_up_time || '10:00' })}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border ${on ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}>{label}</button>
                      );
                    })}
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Date *</label>
                      <input type="date" required value={followupForm.follow_up_date} onChange={(e) => setFollowupForm({ ...followupForm, follow_up_date: e.target.value })} className={ic} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Time <span className="text-gray-400 normal-case">(blank = 10:00 AM)</span></label>
                      <input type="time" value={followupForm.follow_up_time} onChange={(e) => setFollowupForm({ ...followupForm, follow_up_time: e.target.value })} className={ic} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Assigned To * <span className="text-gray-400 normal-case">(gets the reminders)</span></label>
                      <select required value={followupForm.assigned_to || ''} onChange={(e) => setFollowupForm({ ...followupForm, assigned_to: e.target.value })} className={ic}>
                        <option value="">Select user…</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.full_name}{u.id === me.id ? ' (me)' : ''}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Remind</label>
                      <select value={followupForm.remind_before_minutes} onChange={(e) => setFollowupForm({ ...followupForm, remind_before_minutes: Number(e.target.value) })} className={ic}>
                        {FOLLOWUP_REMIND_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                      <select value={followupForm.type} onChange={(e) => setFollowupForm({ ...followupForm, type: e.target.value })} className={ic}>
                        <option value="call">Call</option>
                        <option value="email">Email</option>
                        <option value="whatsapp">WhatsApp</option>
                        <option value="meeting">Meeting</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                      <input type="text" placeholder="Notes" value={followupForm.notes} onChange={(e) => setFollowupForm({ ...followupForm, notes: e.target.value })} className={ic} />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => { setShowFollowupForm(false); setEditFollowup(null); }} className="flex-1 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm">Cancel</button>
                    <button type="submit" className="flex-1 py-2 rounded-lg text-white text-sm" style={{ background: '#0f1f45' }}>{editFollowup ? 'Update' : 'Save'}</button>
                  </div>
                </form>
              )}
              {showFollowupForm ? null : followups.length === 0 ? (
                <div className="text-center py-8 text-gray-400"><p className="text-sm">No follow-ups scheduled yet.</p></div>
              ) : (
                <div className="space-y-3">
                  {followups.map((f) => (
                    <div key={f.id} className={`bg-white border rounded-xl p-4 ${isFollowupOverdue(f) ? 'border-red-300' : 'border-gray-200'}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <span className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">{typeIcon(f.type)}</span>
                          <div>
                            <p className="text-sm font-medium text-gray-800 capitalize">{f.type}</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {new Date(f.follow_up_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                              {f.follow_up_time && ` at ${f.follow_up_time.slice(0, 5)}`}
                            </p>
                            {f.notes && <p className="text-xs text-gray-600 mt-1">{f.notes}</p>}
                            <p className="text-xs text-gray-400 mt-1">
                              <span className="font-medium text-gray-600">Assigned: {f.assigned_to_name || '—'}</span> · set by {f.created_by_name}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isFollowupOverdue(f) && <span className="px-2 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">⚠ Overdue</span>}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${f.status === 'done' ? 'bg-green-100 text-green-700' : f.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {f.status}
                          </span>
                          {canEdit && f.status === 'pending' && <SnoozeMenu onSnooze={(body) => handleFollowupSnooze(f.id, body)} />}
                          {canEdit && f.status !== 'done' && (
                            <button onClick={() => { setEditFollowup(f); setFollowupForm({ follow_up_date: String(f.follow_up_date).slice(0, 10), follow_up_time: (f.follow_up_time || '').slice(0, 5), type: f.type, notes: f.notes || '', assigned_to: f.assigned_to || lead.assigned_to || me.id || '', remind_before_minutes: f.remind_before_minutes ?? 15 }); setShowFollowupForm(true); }}
                              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"><IconEdit /> Edit</button>
                          )}
                          {canEdit && f.status === 'pending' && (
                            <button onClick={() => handleFollowupStatus(f.id, 'done')} className="flex items-center gap-1 text-xs text-green-600 hover:text-green-800 font-medium"><IconCheck /> Done</button>
                          )}
                          {canFollowupDelete && (
                            <button onClick={() => handleFollowupDelete(f)} title="Delete follow-up" className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 font-medium"><IconTrash /> Delete</button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'quotation' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-700">Proposals & Quotations</h3>
                {canCreate && <button onClick={() => {
                    if (showProposalForm) { setShowProposalForm(false); return; }
                    // Force services to be picked on the Lead itself first — a
                    // quotation with no idea which service it's for is exactly
                    // the "which actual service from the master?" confusion
                    // this is meant to prevent.
                    const leadServiceNames = (lead.services || '').split(',').map(s => s.trim()).filter(Boolean);
                    if (leadServiceNames.length === 0) {
                      showToast('Add at least one service to this lead first (Edit Lead → Services) before creating a quotation.', 'error');
                      return;
                    }
                    // Pre-fill quotation items straight from the lead's selected
                    // services, pulling description/rate from the Services
                    // Master — staff can still edit/remove/add lines from there.
                    const items = leadServiceNames.map(name => {
                      const svc = services.find(s => s.name === name);
                      return svc
                        ? { service_id: svc.id, particular: svc.name, description: svc.invoice_description || svc.description || '', quantity: 1, rate: svc.default_amount || 0, discount_type: 'none', discount_value: 0 }
                        : { service_id: null, particular: name, description: '', quantity: 1, rate: 0, discount_type: 'none', discount_value: 0 };
                    });
                    setEditProposal(null);
                    setProposalForm({ items, status: 'draft', notes: '', terms: '', valid_till: '' });
                    setShowProposalForm(true);
                  }}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium"
                  style={showProposalForm ? { background: '#fff', color: '#475569', border: '1px solid #e2e8f0' } : { background: '#0f1f45', color: '#fff' }}>
                  {showProposalForm ? 'Cancel' : '+ Add Quotation'}
                </button>}
              </div>
              {showProposalForm && (() => {
                const QT = DT;
                const edItems = proposalForm.items.map(toEditorLine);
                const grand = computeQuoteTotals(edItems, gstReg).total;
                const inp = quoteInputStyle(QT);
                return (
                  <QuotationShell title={editProposal ? 'Edit Quotation' : 'New Quotation'} quoteNo={editProposal?.quote_number}
                    onClose={() => { setShowProposalForm(false); setEditProposal(null); }}
                    total={grand} onSave={handleAddProposal} saving={savingProposal} saveLabel={editProposal ? 'Update Quotation' : 'Create Quotation'} T={QT} darkMode={darkMode}>
                    <QuoteSection n={1} title="Quotation Details" T={QT}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
                        <div style={{ gridColumn: 'span 2' }}>
                          <QuoteLabel T={QT}>Prepared For</QuoteLabel>
                          <div style={{ ...inp, background: QT.bg3, color: QT.text2 }}>{lead.company || lead.name}{lead.company && lead.name ? ` · ${lead.name}` : ''} <span style={{ color: QT.text3 }}>({lead.lead_id})</span></div>
                        </div>
                        <div>
                          <SmartDatePicker label="Valid Till" value={proposalForm.valid_till || ''} onChange={v => setProposalForm(f => ({ ...f, valid_till: v }))} />
                        </div>
                        <div>
                          <QuoteLabel T={QT}>Status</QuoteLabel>
                          <select value={proposalForm.status} onChange={e => setProposalForm({ ...proposalForm, status: e.target.value })} style={inp}>
                            <option value="draft">Draft</option><option value="sent">Sent</option><option value="accepted">Accepted</option><option value="rejected">Rejected</option>
                          </select>
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                          <QuoteLabel T={QT}>Notes <span style={{ fontWeight: 400 }}>(shown on the quotation)</span></QuoteLabel>
                          <textarea rows="3" value={proposalForm.notes} onChange={e => setProposalForm({ ...proposalForm, notes: e.target.value })} placeholder="e.g. Government fees extra, at actuals" style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                          <QuoteLabel T={QT}>Terms &amp; Conditions <span style={{ fontWeight: 400 }}>(blank uses the firm default from Billing Settings)</span></QuoteLabel>
                          <textarea rows="3" value={proposalForm.terms} onChange={e => setProposalForm({ ...proposalForm, terms: e.target.value })} placeholder={'e.g. 1. Valid for 30 days.\n2. 50% advance required.'} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
                        </div>
                      </div>
                    </QuoteSection>
                    <QuotationItemsSection n={2} items={edItems} onChange={(eds) => setProposalForm(f => ({ ...f, items: eds.map(fromEditorLine) }))} services={services} withScope gstRegistered={gstReg} T={QT} darkMode={darkMode} />
                  </QuotationShell>
                );
              })()}

              {proposals.length === 0 ? (
                <div className="text-center py-8 text-gray-400"><p className="text-sm">No proposals created yet.</p></div>
              ) : (
                <div className="space-y-4">
                  {proposals.map((p) => (
                    <div key={p.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-bold text-gray-800">{p.title}</p>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${proposalStatusColor(p.status)}`}>{p.status}</span>
                          </div>
                          <p className="text-xs text-blue-600 font-medium mt-0.5">#{p.quote_number || 'Generating...'}</p>
                        </div>
                        <p className="text-lg font-bold text-green-600">
                          {quoteTotals(itemsFromProposal(p), gstReg).total ? `₹${quoteTotals(itemsFromProposal(p), gstReg).total.toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : 'Not specified'}
                        </p>
                      </div>
                      {itemsFromProposal(p).length > 0 && (
                        <div className="p-4 border-b space-y-1.5">
                          {itemsFromProposal(p).map((it, i) => (
                            <div key={i} className="flex items-start justify-between gap-3 text-xs">
                              <div>
                                <span className="font-medium text-gray-700">{it.particular}</span>
                                {isGovt(it) && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700">{isPassThrough(it) ? 'Govt fee · no GST' : 'Govt fee'}</span>}
                                {it.description && <span className="text-gray-500"> — {it.description}</span>}
                              </div>
                              <span className="text-gray-600 flex-shrink-0">{it.quantity} × ₹{Number(it.rate||0).toLocaleString('en-IN')}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="p-3 flex items-center gap-2 flex-wrap">
                        {canExport && <button onClick={() => handleDownloadQuotation(p)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-700 hover:bg-gray-50"><IconDownload /> Download PDF</button>}
                        {canEdit && <button onClick={() => handleSendEmail(p)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-blue-200 text-blue-700 hover:bg-blue-50"><IconMail /> Send Email</button>}
                        {canEdit && <button onClick={() => handleSendWhatsApp(p)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-green-200 text-green-700 hover:bg-green-50"><IconWhatsApp /> WhatsApp</button>}
                        {canEdit && (p.status === 'draft' || p.status === 'sent') && (
                          <>
                            <button onClick={() => handleQuotationDecision(p, 'accepted')}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-green-500 text-green-700 hover:bg-green-50 font-semibold"><IconCheck /> Mark Accepted</button>
                            <button onClick={() => handleQuotationDecision(p, 'rejected')}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-red-200 text-red-600 hover:bg-red-50"><IconX /> Mark Rejected</button>
                            <button onClick={() => { setEditProposal(p); setProposalForm({ items: itemsFromProposal(p).length ? itemsFromProposal(p) : [emptyQuotationItem()], status: p.status, notes: p.notes || '', terms: p.terms || '', valid_till: p.valid_till ? String(p.valid_till).slice(0, 10) : '' }); setShowProposalForm(true); }}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-orange-200 text-orange-700 hover:bg-orange-50"><IconEdit /> Edit</button>
                          </>
                        )}
                        {canEdit && p.status === 'accepted' && !p.converted_invoice_id && (
                          <>
                            <button onClick={() => goToInvoice(p)}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-green-500 text-green-700 hover:bg-green-50 font-semibold"><IconSend /> Create Invoice</button>
                            <button onClick={() => { setEditProposal(p); setProposalForm({ items: itemsFromProposal(p).length ? itemsFromProposal(p) : [emptyQuotationItem()], status: p.status, notes: p.notes || '', terms: p.terms || '', valid_till: p.valid_till ? String(p.valid_till).slice(0, 10) : '' }); setShowProposalForm(true); }}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-orange-200 text-orange-700 hover:bg-orange-50"><IconEdit /> Edit</button>
                            <button onClick={() => handleQuotationDecision(p, 'draft')}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50"><IconX /> Revert to Draft</button>
                          </>
                        )}
                        {p.status === 'accepted' && p.converted_invoice_id && (
                          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-green-50 text-green-700"><IconCheck /> Invoice Created — locked</span>
                        )}
                        {p.status === 'rejected' && (
                          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-50 text-red-600"><IconX /> Rejected</span>
                        )}
                        <span className="ml-auto text-xs text-gray-400">By {p.created_by_name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Creation log — who/when this lead was captured (mirrors the Task "Created by" footer) */}
          {(() => {
            const when = lead.created_at ? new Date(lead.created_at).toLocaleString('en-IN', { timeZone:'Asia/Kolkata', day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', hour12:true }) : null;
            const name = lead.created_by_name || 'Website / auto-capture';
            return (
              <div className="flex items-center gap-2 flex-wrap mt-5 px-1 text-xs text-gray-400">
                <span>Created by</span>
                <span style={{ width:24, height:24, borderRadius:'50%', overflow:'hidden', flexShrink:0, display:'inline-flex', alignItems:'center', justifyContent:'center', background: lead.created_by_avatar ? 'transparent' : 'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:10, fontWeight:700 }}>
                  {lead.created_by_avatar
                    ? <img src={lead.created_by_avatar.startsWith('http') ? lead.created_by_avatar : `${BASE}${lead.created_by_avatar}`} alt={name} style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'center 20%' }} />
                    : name.charAt(0).toUpperCase()}
                </span>
                <span className="font-semibold text-gray-600">{name}</span>
                {when && (<>
                  <span>on</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  <span>{when}</span>
                </>)}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

// ─── Create Task From Lead Modal ──────────────────────────────────────────────
// ─── Assign User Modal — same visual pattern as Clients.js's AssignUserModal.
// A lead can have several owners; the first one picked is the primary owner. ──
function AssignLeadModal({ lead, token, onClose, onSuccess, darkMode, T }) {
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(() => (Array.isArray(lead.assignee_ids) && lead.assignee_ids.length ? lead.assignee_ids : (lead.assigned_to ? [lead.assigned_to] : [])).map(Number));
  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const [saving, setSaving] = useState(false);
  const [toasts, showToast] = useToasts();
  const { canEdit, deny } = useCrmAccess(showToast);

  useEffect(() => {
    fetch(`${BASE}/api/users`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setUsers(Array.isArray(d) ? d : [])).catch(() => {});
  }, [token]);

  const handleSave = async () => {
    if (!canEdit) { deny('edit'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/leads/${lead.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...lead, assigned_to: selected[0] || null, assignee_ids: selected })
      });
      if (res.ok) onSuccess();
      else showToast('Failed to assign lead', 'error');
    } catch { showToast('Server error', 'error'); } finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:60, padding:16 }}>
      <ToastStack toasts={toasts} />
      <div style={{ background:T.bg2, borderRadius:16, width:'100%', maxWidth:380, boxShadow:'0 24px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding:'16px 20px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <p style={{ fontSize:14, fontWeight:700, color:T.text, margin:0 }}>Assign Lead <span style={{ fontSize:11, fontWeight:500, color:T.text3 }}>· one or more users</span></p>
            <p style={{ fontSize:12, color:T.text3, margin:'2px 0 0' }}>{lead.company || lead.name}</p>
          </div>
          <button onClick={onClose} style={{ width:32, height:32, borderRadius:'50%', border:'none', background:'none', color:T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
            <IconX />
          </button>
        </div>
        <div style={{ padding:16, display:'flex', flexDirection:'column', gap:6, maxHeight:288, overflowY:'auto' }}>
          <label onClick={() => setSelected([])}
            style={{ display:'flex', alignItems:'center', gap:12, padding:12, borderRadius:12, cursor:'pointer', border:`1px solid ${!selected.length ? '#93c5fd' : (darkMode?T.border:'#f1f5f9')}`, background: !selected.length ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent' }}>
            <div style={{ width:20, height:20, borderRadius:6, border:`2px solid ${!selected.length ? '#0f1f45' : (darkMode?T.border2:'#d1d5db')}`, background: !selected.length ? '#0f1f45' : 'transparent', flexShrink:0 }}/>
            <span style={{ fontSize:13, color:T.text3, fontStyle:'italic' }}>Unassigned</span>
          </label>
          {users.map(u => {
            const isSel = selected.includes(Number(u.id));
            const isPrimary = selected[0] === Number(u.id);
            return (
              <label key={u.id} onClick={() => toggle(Number(u.id))}
                style={{ display:'flex', alignItems:'center', gap:12, padding:12, borderRadius:12, cursor:'pointer', border:`1px solid ${isSel ? '#93c5fd' : (darkMode?T.border:'#f1f5f9')}`, background: isSel ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent' }}>
                <div style={{ width:20, height:20, borderRadius:6, border:`2px solid ${isSel ? '#0f1f45' : (darkMode?T.border2:'#d1d5db')}`, background: isSel ? '#0f1f45' : 'transparent', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {isSel && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5"><polyline points="20 6 9 17 4 12"/></svg>}
                </div>
                <div style={{ flex:1 }}>
                  <p style={{ fontSize:13, fontWeight:500, color:T.text, margin:0 }}>{u.full_name || u.name}</p>
                  {u.role && <p style={{ fontSize:11, color:T.text3, margin:0, textTransform:'capitalize' }}>{u.role}</p>}
                </div>
                {isSel && <span style={{ fontSize:11, fontWeight:600, color:'#3b82f6', background: darkMode?'rgba(59,130,246,0.15)':'#dbeafe', padding:'2px 8px', borderRadius:20 }}>{isPrimary && selected.length > 1 ? 'Primary' : 'Assigned'}</span>}
              </label>
            );
          })}
          {users.length === 0 && <p style={{ textAlign:'center', color:T.text3, fontSize:13, padding:'16px 0' }}>No users found</p>}
        </div>
        <div style={{ padding:16, borderTop:`1px solid ${T.border}`, display:'flex', gap:10 }}>
          <button onClick={onClose} style={{ flex:1, padding:'9px 0', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            style={{ flex:1, padding:'9px 0', borderRadius:9, border:'none', background:'#0f1f45', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:saving?0.6:1 }}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateTaskFromLeadModal({ lead, token, onClose, onSuccess, darkMode, T }) {
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const [toasts, showToast] = useToasts();
  const { canIn } = useCrmAccess(showToast);
  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    service_id: '',
    client_id: lead.client_id ? String(lead.client_id) : '',
    assigned_to: lead.assigned_to ? String(lead.assigned_to) : '',
    due_date: '',
    priority: 'medium',
    notes: lead.company || lead.name || '',
    billing_amount: '',
    referral_commission_type: '',
    referral_commission_value: '',
  });
  const is = { width:'100%', border:`1px solid ${T.inputBorder||T.border2}`, borderRadius:9, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg||T.bg2, color:T.text, boxSizing:'border-box' };

  useEffect(() => {
    Promise.all([
      fetch(`${BASE}/api/services`, { headers:{ Authorization:`Bearer ${token}` } }).then(r=>r.json()).catch(()=>[]),
      fetch(`${BASE}/api/users`, { headers:{ Authorization:`Bearer ${token}` } }).then(r=>r.json()).catch(()=>[]),
      fetch(`${BASE}/api/clients`, { headers:{ Authorization:`Bearer ${token}` } }).then(r=>r.json()).catch(()=>[]),
    ]).then(([s, u, c]) => {
      setServices(Array.isArray(s) ? s : []);
      setUsers(Array.isArray(u) ? u : []);
      setClients(Array.isArray(c) ? c : []);
    });
  }, [token]);

  // Auto-fill billing amount when service selected
  const handleServiceChange = (e) => {
    const svc = services.find(s => String(s.id) === e.target.value);
    setForm(f => ({ ...f, service_id: e.target.value, billing_amount: svc?.default_amount || svc?.default_billing_amount || '' }));
  };

  const handleCreate = async () => {
    if (!canIn('tasks', 'create')) { showToast("You don't have permission to create tasks. Ask your admin.", 'error'); return; }
    if (!form.service_id) return showToast('Please select a service', 'error');
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({
          service_id: form.service_id,
          client_id: form.client_id || null,
          assigned_to: form.assigned_to || null,
          due_date: form.due_date || null,
          priority: form.priority,
          notes: form.notes,
          billing_amount: form.billing_amount || null,
          referral_commission_type: form.referral_commission_type || null,
          referral_commission_value: form.referral_commission_value || null,
          lead_id: lead.id,
          title: `${services.find(s=>String(s.id)===form.service_id)?.name || 'Task'} — ${lead.company || lead.name || 'Lead'}`,
        }),
      });
      if (res.ok) { onSuccess(); }
      else { const d = await res.json(); showToast(d.message || 'Failed to create task', 'error'); }
    } catch (_) { showToast('Server error', 'error'); }
    setSaving(false);
  };

  const grouped = services.reduce((acc, s) => {
    const cat = s.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:16 }}>
      <ToastStack toasts={toasts} />
      <div style={{ background:T.bg2, borderRadius:20, width:'100%', maxWidth:520, maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 24px 60px rgba(0,0,0,0.4)' }}>
        {/* Header */}
        <div style={{ padding:'18px 24px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'20px 20px 0 0', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>Create Task from Lead</h3>
            <p style={{ fontSize:12, color:'rgba(255,255,255,0.6)', margin:'3px 0 0' }}>{lead.company || lead.name || 'Lead'} · {lead.lead_id}</p>
          </div>
          <button onClick={onClose} style={{ width:30, height:30, borderRadius:8, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto', padding:'20px 24px', background:T.bg3, display:'flex', flexDirection:'column', gap:14, scrollbarWidth:'none' }}>

          {/* Lead info banner */}
          <div style={{ background:darkMode?'rgba(59,130,246,0.1)':'#eff6ff', border:`1px solid ${darkMode?'rgba(59,130,246,0.2)':'#bfdbfe'}`, borderRadius:10, padding:'10px 14px', display:'flex', gap:16 }}>
            <div>
              <p style={{ fontSize:11, color:T.text3, margin:'0 0 2px', textTransform:'uppercase', fontWeight:600 }}>Lead</p>
              <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>{lead.company || lead.name || '—'}</p>
            </div>
            <div>
              <p style={{ fontSize:11, color:T.text3, margin:'0 0 2px', textTransform:'uppercase', fontWeight:600 }}>Stage</p>
              <p style={{ fontSize:13, fontWeight:600, color:'#3b82f6', margin:0 }}>{(LEAD_STAGES.find(st => st.value === lead.stage) || {}).name || String(lead.stage || '').replace(/_/g, ' ')}</p>
            </div>
            {lead.referred_by && (
              <div>
                <p style={{ fontSize:11, color:T.text3, margin:'0 0 2px', textTransform:'uppercase', fontWeight:600 }}>Referred By</p>
                <p style={{ fontSize:13, fontWeight:600, color:'#f97316', margin:0 }}>{lead.referred_by_name || lead.referred_by}</p>
              </div>
            )}
          </div>

          {/* Service */}
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Service *</label>
            <select value={form.service_id} onChange={handleServiceChange} style={is}>
              <option value="">Select service...</option>
              {Object.entries(grouped).map(([cat, svcs]) => (
                <optgroup key={cat} label={cat}>
                  {svcs.map(s => <option key={s.id} value={s.id}>{s.name}{s.default_amount ? ` — ₹${Number(s.default_amount).toLocaleString('en-IN')}` : ''}</option>)}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Client */}
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Link to Client (optional)</label>
            <select value={form.client_id} onChange={e=>setForm(f=>({...f,client_id:e.target.value}))} style={is}>
              <option value="">No client yet</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.client_name || c.display_name || c.company_name}</option>)}
            </select>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            {/* Assign To */}
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Assign To</label>
              <select value={form.assigned_to} onChange={e=>setForm(f=>({...f,assigned_to:e.target.value}))} style={is}>
                <option value="">Unassigned</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.name}</option>)}
              </select>
            </div>

            {/* Due Date */}
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Due Date</label>
              <input type="date" value={form.due_date} onChange={e=>setForm(f=>({...f,due_date:e.target.value}))} style={is} />
            </div>

            {/* Priority */}
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Priority</label>
              <select value={form.priority} onChange={e=>setForm(f=>({...f,priority:e.target.value}))} style={is}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            {/* Billing Amount */}
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Billing Amount (₹)</label>
              <input type="number" value={form.billing_amount} onChange={e=>setForm(f=>({...f,billing_amount:e.target.value}))} placeholder="Auto from service" style={is} />
            </div>
          </div>

          {/* Referral Commission (task-level) — only relevant when the lead was referred */}
          {lead.referred_by && (
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Referral Commission for this Task</label>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <select value={form.referral_commission_type} onChange={e=>setForm(f=>({...f,referral_commission_type:e.target.value}))} style={is}>
                  <option value="">Inherit (service / partner)</option>
                  <option value="percentage">Percentage %</option>
                  <option value="fixed">Fixed ₹</option>
                  <option value="none">No commission</option>
                </select>
                <input type="number" min="0" step="0.01"
                  disabled={!form.referral_commission_type || form.referral_commission_type==='none'}
                  placeholder={form.referral_commission_type==='percentage' ? '% rate' : '₹ amount'}
                  value={form.referral_commission_value}
                  onChange={e=>setForm(f=>({...f,referral_commission_value:e.target.value}))} style={is} />
              </div>
              <p style={{ fontSize:11, color:T.text3, marginTop:5 }}>Referred by {lead.referred_by_name || lead.referred_by}. Leave as inherit to use the service or partner rate.</p>
            </div>
          )}

          {/* Notes */}
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Notes</label>
            <input type="text" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Task notes..." style={is} />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding:'14px 24px', borderTop:`1px solid ${T.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', background:T.bg2, borderRadius:'0 0 20px 20px', flexShrink:0 }}>
          <p style={{ fontSize:12, color:T.text3, margin:0 }}>Task will appear in Tasks module</p>
          <div style={{ display:'flex', gap:10 }}>
            <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}>Cancel</button>
            <button onClick={handleCreate} disabled={saving||!form.service_id}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 22px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:(saving||!form.service_id)?0.6:1 }}>
              {saving && <span style={{ width:13, height:13, border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite' }}/>}
              {saving ? 'Creating...' : 'Create Task'}
            </button>
          </div>
        </div>
      </div>
      </div>
  );
}

function ReferralPartnerSelect({ value, onChange, token, darkMode, T }) {
  const [partners, setPartners] = useState([]);
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  useEffect(() => {
    fetch(`${BASE}/api/referral-partners?status=active`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json())
      .then(d => setPartners(Array.isArray(d) ? d : []))
      .catch(() => setPartners([]));
  }, [token]);

  const items = [{ value: '', name: 'None / Direct' }, ...partners.map(p => ({ value: p.referral_partner_id, name: p.name, sub: p.referral_partner_id }))];
  return <PicklistDropdown items={items} value={value || ''} onChange={onChange} placeholder="None / Direct" darkMode={darkMode} T={T} />;
}

export default CRM;